import React, { useState, useEffect, useRef, useCallback } from "react";
import {
  Table,
  Button,
  Space,
  Tag,
  Modal,
  Form,
  Input,
  Select,
  DatePicker,
  InputNumber,
  Slider,
  Upload,
  message,
  Tooltip,
  Row,
  Col,
  Typography,
  Image,
  Checkbox,
  Alert,
  Radio,
  Drawer,
  Dropdown,
  Popover,
  Empty,
  Steps,
  Tabs,
  Segmented,
} from "antd";
import {
  PlusOutlined,
  EditOutlined,
  UploadOutlined,
  DownloadOutlined,
  SyncOutlined,
  DownOutlined,
  FilterOutlined,
  SearchOutlined,
  PictureOutlined,
  PercentageOutlined,
  FieldTimeOutlined,
  CheckCircleOutlined,
} from "@ant-design/icons";
import { useNavigate, useLocation } from "react-router-dom";
import axios from "../../utils/axios";
import dayjs from "dayjs";
import isBetweenPlugin from "dayjs/plugin/isBetween";
import relativeTime from "dayjs/plugin/relativeTime";
import ReactCrop from "react-image-crop";
import "react-image-crop/dist/ReactCrop.css";
import RichTextEditor from "../../components/RichTextEditor";
import "../../styles/richtext.css";
import {
  normalizeDescriptionForDisplay,
  normalizeTermsForDisplay,
  hasVisibleHtmlContent,
} from "../../utils/termsAndConditionsDisplay";
import { mergePricingTiersForDisplay } from "../../utils/pricingTiers";
import { responsiveFilterSelectProps } from "../../utils/responsiveFilterSelect";
import {
  COUNTRIES_CURRENCY_MAP,
  COUNTRY_OPTIONS,
  VOUCHER_CATEGORY_OPTIONS,
  VOUCHER_TYPE_OPTIONS,
  CONSUMPTION_TYPE_OPTIONS,
  voucherTypeSelectOptions,
  consumptionTypeSelectOptions,
  CURRENCY_OPTIONS,
  getConsumptionCsvCopy,
  isPoolBackedVoucher,
  isSharedUrlVoucher,
  URL_MODE_OPTIONS,
  VOUCHER_EXPIRY_MODES,
  DEFAULT_EXTERNAL_REDEMPTION_DAYS,
  resolveVoucherExpiryMode,
} from "../../constants/voucherConstants";
import {
  PageHeader,
  FilterBar,
  TableCard,
  Section,
  StatusTag,
  MODAL_WIDTH,
  DRAWER_WIDTH,
  formModalProps,
  tableDefaults,
  paginationDefaults,
} from "../../components/admin";
import { showApiError } from "../../utils/apiError";
import { formatDate, formatDateTime, fromNow } from "../../utils/dateFormat";
import { formatMoney } from "../../utils/formatMoney";
import "./VoucherManagement.css";

/** Admin → backend → provider scripts can run for minutes; must exceed global axios 10s and align with backend ZHONGMIN_SCRIPT_TIMEOUT_MS (default 600000). */
const ZHONGMIN_SCRIPT_HTTP_TIMEOUT_MS = 600_000;

const INTERNAL_VOUCHER_PROVIDER_KEY = "vouchain";
/** Manually uploaded WOGI catalogue (internal, fixed-date vouchers). */
const WOGI_VOUCHER_PROVIDER_KEY = "wogi";

/** Suppliers reached through Voucher Provider Service, in sync order. */
const SYNC_PROVIDERS = ["gowabi", "giftee", "zhongmin", "iak"];
const PROVIDER_LABELS = {
  gowabi: "GoWabi",
  giftee: "Giftee",
  zhongmin: "Zhongmin",
  iak: "IAK",
};

/** Shown by antd Image when a voucher image URL fails to load. */
const IMAGE_FALLBACK =
  "data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAMIAAADDCAYAAADQvc6UAAABRWlDQ1BJQ0MgUHJvZmlsZQAAKJFjYGASSSwoyGFhYGDIzSspCnJ3UoiIjFJgf8LAwSDCIMogwMCcmFxc4BgQ4ANUwgCjUcG3awyMIPqyLsis7PPOq3QdDFcvjV3jOD1boQVTPQrgSkktTgbSf4A4LbmgqISBgTEFyFYuLykAsTuAbJEioKOA7DkgdjqEvQHEToKwj4DVhAQ5A9k3gGyB5IxEoBmML4BsnSQk8XQkNtReEOBxcfXxUQg1Mjc0dyHgXNJBSWpFCYh2zi+oLMpMzyhRcASGUqqCZ16yno6CkYGRAQMDKMwhqj/fAIcloxgHQqxAjIHBEugw5sUIsSQpBobtQPdLciLEVJYzMPBHMDBsayhILEqEO4DxG0txmrERhM29nYGBddr//5/DGRjYNRkY/l7////39v///y4Dmn+LgeHANwDrkl1AuO+pmgAAADhlWElmTU0AKgAAAAgAAYdpAAQAAAABAAAAGgAAAAAAAqACAAQAAAABAAAAwqADAAQAAAABAAAAwwAAAAD9b/HnAAAHlklEQVR4Ae3dP3Ik1RnG4W+FgYxN";

/** Voucher code statuses, as returned by the codes endpoints. */
const CODE_STATUS = {
  available: { label: "Available", tone: "success" },
  "sold-not-redeemed": { label: "Sold", tone: "warning" },
  redeemed: { label: "Redeemed", tone: "info" },
  expired: { label: "Expired", tone: "danger" },
};

const renderCodeStatus = (status) => {
  const entry = CODE_STATUS[status];
  return (
    <StatusTag tone={entry?.tone || "default"}>
      {entry?.label || status || "-"}
    </StatusTag>
  );
};

function formatVoucherProviderLabel(providerName) {
  const key = String(providerName || "").trim();
  if (key.toLowerCase() === INTERNAL_VOUCHER_PROVIDER_KEY)
    return "Internal vouchers";
  if (key.toLowerCase() === WOGI_VOUCHER_PROVIDER_KEY) return "WOGI";
  return key;
}

/**
 * Says, in one line, how the voucher being edited will actually expire.
 *
 * The two modes are mutually exclusive downstream in VIO, but the form always
 * keeps both sets of inputs: the dates double as the catalogue shelf window
 * even when a relative window is what the member gets. Without this summary the
 * form gives no clue which one wins.
 */
function ExpiryModeSummary({ redemptionDays, isExternalVoucher }) {
  const days = Number(redemptionDays);
  const hasWindow = Number.isInteger(days) && days >= 1;

  if (hasWindow) {
    return (
      <Alert
        type="info"
        showIcon
        className="va-alert"
        message={`Expires ${days} days after purchase`}
        description={
          "The member's voucher expires " + days + " days from the day they buy it. "
          + "The dates below are the catalogue shelf window — how long it stays "
          + "listed for sale — not the member's expiry. Clear the redemption "
          + "window to go back to a fixed expiry date."
        }
      />
    );
  }

  if (isExternalVoucher) {
    return (
      <Alert
        type="warning"
        showIcon
        className="va-alert"
        message={`Expires ${DEFAULT_EXTERNAL_REDEMPTION_DAYS} days after purchase (inherited default)`}
        description={
          "No redemption window is set, so VIO applies its approved "
          + DEFAULT_EXTERNAL_REDEMPTION_DAYS + "-day default. Set one below to "
          + "override it."
        }
      />
    );
  }

  return (
    <Alert
      type="info"
      showIcon
      className="va-alert"
      message="Expires on a fixed date"
      description={
        "Every member's voucher expires on the Valid Until date below, whenever "
        + "they bought it. Set a redemption window to switch to "
        + "\u201Cvalid N days from purchase\u201D instead."
      }
    />
  );
}

dayjs.extend(isBetweenPlugin);
dayjs.extend(relativeTime);

const { Text } = Typography;
const { Option } = Select;

/** Blur Quill editors and close Snow toolbar pickers before stacking another modal. */
const dismissQuillUi = () => {
  if (typeof document === "undefined") return;
  document.querySelectorAll(".ql-editor").forEach((el) => {
    el.blur?.();
  });
  const sel = typeof window !== "undefined" ? window.getSelection?.() : null;
  if (sel?.rangeCount) sel.removeAllRanges();
  document
    .querySelectorAll(".ql-toolbar .ql-picker.ql-expanded")
    .forEach((picker) => picker.classList.remove("ql-expanded"));
};

// Tier pricing preview component
// Client price = settlementPrice * tier multiplier. Settlement price is derived
// from cost × (1 + markup%). `basePrice` is shown for context but does not feed
// the calculation.
const TierPricingPreview = ({
  basePrice,
  costPrice,
  markupPercent,
  settlementPrice,
  pricingTiers,
  currency,
  className,
}) => {
  const derivedSettlement = (() => {
    const cost = Number(costPrice) || 0;
    const hasMarkup =
      markupPercent !== null &&
      markupPercent !== undefined &&
      markupPercent !== "";
    if (hasMarkup && cost > 0) {
      return computeSettlementFromMarkup(cost, markupPercent);
    }
    if (Number.isFinite(Number(settlementPrice)) && Number(settlementPrice) > 0) {
      return Number(settlementPrice);
    }
    return computeSettlementFromMarkup(cost, 0);
  })();

  const validBasis = derivedSettlement > 0 ? derivedSettlement : Number(basePrice) || 0;

  const sortedTiers =
    pricingTiers && pricingTiers.length > 0
      ? [...pricingTiers].sort((a, b) => b.multiplier - a.multiplier)
      : [];

  const markupDisplay =
    markupPercent === null || markupPercent === undefined || markupPercent === ""
      ? derivedSettlement > 0 && Number(costPrice) > 0
        ? `${Math.round((derivedSettlement / Number(costPrice) - 1) * 10000) / 10000}%`
        : "—"
      : `${Number(markupPercent)}%`;

  const money = (value) => formatMoney(value, currency);

  return (
    <div className={`va-panel${className ? ` ${className}` : ""}`}>
      <div className="va-panel__title">Client tier prices</div>
      {sortedTiers.length > 0 ? (
        <div className="va-tier-grid">
          {sortedTiers.map((tier) => {
            const tierPrice = validBasis * tier.multiplier;
            const discount = ((1 - tier.multiplier) * 100).toFixed(0);
            return (
              <React.Fragment key={tier.name}>
                <span className="va-tier-grid__name">{tier.displayName}</span>
                <span className="va-tier-grid__price cell-money">
                  {money(tierPrice)}
                </span>
                <span className="va-tier-grid__delta">
                  {discount === "0"
                    ? "—"
                    : discount > 0
                      ? `-${discount}%`
                      : `+${Math.abs(discount)}%`}
                </span>
              </React.Fragment>
            );
          })}
        </div>
      ) : (
        <span className="muted">No pricing tiers configured</span>
      )}
      <div className="va-tier-foot">
        Cost {money(costPrice || 0)} · Mark up {markupDisplay} · Settlement{" "}
        <Text strong>{money(validBasis)}</Text>
        {Number(basePrice) > 0 && <span> · Face value {money(basePrice)}</span>}
      </div>
    </div>
  );
};

// Voucher image upload constraints (kept in sync with the hint shown in the form).
const IMAGE_MAX_SIZE_MB = 5;
const ACCEPTED_IMAGE_TYPES = ["image/jpeg", "image/png", "image/webp"];
const ACCEPTED_IMAGE_ACCEPT = ".jpg,.jpeg,.png,.webp,image/jpeg,image/png,image/webp";
const IMAGE_UPLOAD_HINT = `Supported formats: JPG, PNG, WEBP. Max size: ${IMAGE_MAX_SIZE_MB}MB.`;

// Validate a voucher image's format and size before cropping/upload.
// Returns an error message string when invalid, or null when the file is ok.
const getImageValidationError = (file) => {
  if (!file) return null;
  if (file.type && !ACCEPTED_IMAGE_TYPES.includes(file.type)) {
    return "Unsupported image format. Please upload a JPG, PNG or WEBP file.";
  }
  if (file.size > IMAGE_MAX_SIZE_MB * 1024 * 1024) {
    return `Image must be smaller than ${IMAGE_MAX_SIZE_MB}MB.`;
  }
  return null;
};

// Helper function to create canvas from cropped area
const getCroppedImg = (image, crop, fileName) => {
  const canvas = document.createElement("canvas");
  const ctx = canvas.getContext("2d");

  if (!ctx) {
    throw new Error("Failed to get canvas context");
  }

  const scaleX = image.naturalWidth / image.width;
  const scaleY = image.naturalHeight / image.height;
  const pixelRatio = window.devicePixelRatio;

  canvas.width = crop.width * pixelRatio * scaleX;
  canvas.height = crop.height * pixelRatio * scaleY;

  ctx.setTransform(pixelRatio, 0, 0, pixelRatio, 0, 0);
  ctx.imageSmoothingQuality = "high";

  ctx.drawImage(
    image,
    crop.x * scaleX,
    crop.y * scaleY,
    crop.width * scaleX,
    crop.height * scaleY,
    0,
    0,
    crop.width * scaleX,
    crop.height * scaleY,
  );

  return new Promise((resolve) => {
    canvas.toBlob(
      (blob) => {
        if (!blob) {
          console.error("Canvas is empty");
          return;
        }
        const file = new File([blob], fileName, { type: "image/jpeg" });
        resolve(file);
      },
      "image/jpeg",
      0.9,
    );
  });
};

/** Coerce API / table values for Ant Design InputNumber */
const toFiniteNumber = (x) => {
  if (x === undefined || x === "") return undefined;
  if (x === null) return undefined;
  if (typeof x === "object" && x !== null) {
    if (typeof x.toString === "function") {
      const s = x.toString();
      if (s && s !== "[object Object]") {
        const n = Number(s);
        if (Number.isFinite(n)) return n;
      }
    }
    if (typeof x.$numberDecimal === "string" || typeof x.$numberDecimal === "number") {
      const n = Number(x.$numberDecimal);
      if (Number.isFinite(n)) return n;
    }
  }
  const n = Number(x);
  return Number.isFinite(n) ? n : undefined;
};

const firstFiniteNumberOrZero = (...vals) => {
  for (const v of vals) {
    const n = toFiniteNumber(v);
    if (n !== undefined) return n;
  }
  return 0;
};

/** Settlement price is derived from cost × (1 + markup/100). */
const computeSettlementFromMarkup = (costPrice, markupPercent) => {
  const cost = Number(costPrice);
  if (!Number.isFinite(cost) || cost < 0) return 0;
  const mp = Number(markupPercent);
  const effective = Number.isFinite(mp) && mp >= 0 ? mp : 0;
  return Math.round(cost * (1 + effective / 100) * 100) / 100;
};

const getCostBaseDiscountPercent = (voucher) => {
  const cost = Number(voucher?.costPrice);
  const base = Number(voucher?.basePrice);
  if (!Number.isFinite(cost) || !Number.isFinite(base) || base <= 0) {
    return null;
  }
  return Math.round((1 - cost / base) * 10000) / 100;
};

const getCostBaseRatioPercent = (voucher) => {
  const cost = Number(voucher?.costPrice);
  const base = Number(voucher?.basePrice);
  if (!Number.isFinite(cost) || !Number.isFinite(base) || base <= 0) {
    return null;
  }
  return Math.round((cost / base) * 10000) / 100;
};

// Cost can exceed face value (we pay a premium), which reads as a negative
// discount, so the range filter spans both sides of 0.
const COST_DISCOUNT_SLIDER_MIN = -100;
const COST_DISCOUNT_SLIDER_MAX = 100;
// Admin list endpoint accepts `limit` up to 500. This is the per-request batch
// size, not a total cap: the export keeps paging until the server's reported
// total is covered, so EXPORT_MAX_PAGES is only a runaway backstop.
const EXPORT_PAGE_SIZE = 500;
const EXPORT_MAX_PAGES = 1000;
/**
 * An export page resolves a pricing tier per voucher server-side, so 500 rows
 * can take well over the 10s axios default. Override per request rather than by
 * URL, since the export shares its endpoint with the (fast) table list.
 */
const EXPORT_REQUEST_TIMEOUT_MS = 60_000;

const COST_DISCOUNT_SLIDER_MARKS = {
  [-100]: "-100%",
  [-50]: "-50%",
  0: "0%",
  50: "50%",
  100: "100%",
};

/**
 * Effective markup % for display/editing. DB null means "inherit provider on sync";
 * resolve from provider config, else derive from stored settlement vs cost.
 */
const resolveEffectiveMarkupPercent = (voucher, providerRates = {}) => {
  if (!voucher) return null;
  if (voucher.markupPercent !== null && voucher.markupPercent !== undefined) {
    const stored = Number(voucher.markupPercent);
    if (Number.isFinite(stored) && stored >= 0) return stored;
  }
  const providerName = String(voucher.providerName || "").trim().toLowerCase();
  const fromProvider = providerRates?.[providerName]?.markupPercent;
  if (fromProvider !== null && fromProvider !== undefined) {
    const p = Number(fromProvider);
    if (Number.isFinite(p) && p >= 0) return p;
  }
  const cost = Number(voucher.costPrice);
  const settlement = Number(voucher.settlementPrice);
  if (Number.isFinite(cost) && cost > 0 && Number.isFinite(settlement) && settlement > 0) {
    return Math.round((settlement / cost - 1) * 10000) / 10000;
  }
  return null;
};

const markupPercentIsInherited = (voucher) => {
  if (!voucher) return true;
  if (voucher.markupPercentOverride === true) return false;
  if (voucher.markupPercentOverride === false) return true;
  return voucher.markupPercent === null || voucher.markupPercent === undefined;
};

/** Explicit edit-form fields so values apply after Modal mounts (avoids empty inputs / wrong defaults). */
const buildEditFormValues = (v, providerRates = {}) => {
  if (!v) return {};
  return {
    name: v.name,
    voucherStatus: v.voucherStatus,
    brand: v.brand,
    // Legacy internal vouchers may have empty providerName; surface them as
    // "vouchain" so the Provider field shows "Internal vouchers" not blank.
    providerName:
      v.providerName && String(v.providerName).trim() !== ""
        ? v.providerName
        : v.isExternalVoucher
          ? v.providerName
          : INTERNAL_VOUCHER_PROVIDER_KEY,
    category: v.category,
    description: v.description,
    voucherType: v.voucherType,
    discountPercent: toFiniteNumber(v.discountPercent),
    maxDiscount: toFiniteNumber(v.maxDiscount),
    minSpend: firstFiniteNumberOrZero(v.minSpend),
    consumptionType: v.consumptionType,
    consumptionMessage: v.consumptionMessage || "",
    urlMode: v.urlMode || "unique",
    sharedRedemptionUrl: v.sharedRedemptionUrl || "",
    expiryStartDatetime: v.expiryStartDatetime
      ? dayjs(v.expiryStartDatetime)
      : undefined,
    expiryEndDatetime: v.expiryEndDatetime
      ? dayjs(v.expiryEndDatetime)
      : undefined,
    country: v.country,
    currency: v.currency,
    address: v.address || "",
    latitude: toFiniteNumber(v.latitude),
    longitude: toFiniteNumber(v.longitude),
    totalQuantity: toFiniteNumber(v.totalQuantity),
    maxPurchasePerUser: toFiniteNumber(v.maxPurchasePerUser),
    redemptionValidityDays: toFiniteNumber(v.redemptionValidityDays),
    costPrice: firstFiniteNumberOrZero(v.costPrice),
    basePrice: firstFiniteNumberOrZero(
      v.basePrice,
      v.originalPrice,
      typeof v.normalizedPrice === "number" ? v.normalizedPrice : undefined,
    ),
    // Always show the effective markup (provider default or per-voucher override).
    markupPercent: resolveEffectiveMarkupPercent(v, providerRates),
    priceChangeReason: "",
    termsAndConditions: normalizeTermsForDisplay(v.termsAndConditions || ""),
  };
};

/**
 * URL vouchers: choose between one link per unit (CSV pool) and one link
 * shared by every unit. `locked` once codes exist — the backend refuses to
 * switch modes then, so the UI says so up front.
 */
const UrlModeFields = ({ urlMode, sharedUrl, locked = false }) => {
  const trimmedUrl = String(sharedUrl || "").trim();
  const canOpen = /^https?:\/\/\S+$/i.test(trimmedUrl);
  return (
    <>
      <Form.Item
        name="urlMode"
        label="Link delivery"
        initialValue="unique"
        preserve={false}
        extra={
          locked
            ? "Locked: codes have already been imported or issued for this voucher. Create a new voucher to use the other mode."
            : undefined
        }
      >
        <Radio.Group disabled={locked}>
          <Space direction="vertical">
            {URL_MODE_OPTIONS.map((option) => (
              <Radio key={option.value} value={option.value}>
                {option.label}
              </Radio>
            ))}
          </Space>
        </Radio.Group>
      </Form.Item>
      {urlMode === "shared" && (
        <Form.Item
          label="Shared redemption link"
          required
          extra="Every member who buys this voucher receives this same link. Changing it later only affects units sold after the change."
        >
          <Space.Compact block>
            <Form.Item
              name="sharedRedemptionUrl"
              noStyle
              preserve={false}
              rules={[
                { required: true, message: "Please enter the shared link" },
                {
                  pattern: /^https?:\/\/\S+$/i,
                  message: "Must be a valid http(s) URL",
                },
                { max: 2048, message: "Link cannot exceed 2048 characters" },
              ]}
            >
              <Input placeholder="https://..." />
            </Form.Item>
            <Button
              disabled={!canOpen}
              href={canOpen ? trimmedUrl : undefined}
              target="_blank"
              rel="noopener noreferrer"
            >
              Test link
            </Button>
          </Space.Compact>
        </Form.Item>
      )}
    </>
  );
};

const VoucherManagement = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [vouchers, setVouchers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 20,
    total: 0,
  });

  // Modal states
  const [createModalVisible, setCreateModalVisible] = useState(false);
  const [editModalVisible, setEditModalVisible] = useState(false);
  const [previewModalVisible, setPreviewModalVisible] = useState(false);
  const [detailsModalVisible, setDetailsModalVisible] = useState(false);
  const [syncDrawerOpen, setSyncDrawerOpen] = useState(false);
  // Which sync is running ('all' or a provider key), only to place the spinner.
  const [syncTarget, setSyncTarget] = useState(null);
  const [bulkOnlineModalVisible, setBulkOnlineModalVisible] = useState(false);
  const [previewData, setPreviewData] = useState(null);
  const [previewMode, setPreviewMode] = useState(null); // 'create' or 'edit'
  const [previewLoading, setPreviewLoading] = useState(false);

  // Forms
  const [createForm] = Form.useForm();
  const [editForm] = Form.useForm();
  const [bulkMarkupForm] = Form.useForm();
  const [bulkRedemptionForm] = Form.useForm();

  // Watch form values for real-time preview
  const watchedBasePrice = Form.useWatch("basePrice", createForm);
  const watchedCostPrice = Form.useWatch("costPrice", createForm);
  const watchedMarkupPercent = Form.useWatch("markupPercent", createForm);
  const watchedEditMarkupPercent = Form.useWatch("markupPercent", editForm);
  const watchedBulkMarkupPercent = Form.useWatch("markupPercent", bulkMarkupForm);
  const watchedCreateConsumptionType = Form.useWatch(
    "consumptionType",
    createForm,
  );
  const watchedEditConsumptionType = Form.useWatch(
    "consumptionType",
    editForm,
  );
  const watchedCreateUrlMode = Form.useWatch("urlMode", createForm);
  const watchedEditUrlMode = Form.useWatch("urlMode", editForm);
  const watchedCreateSharedUrl = Form.useWatch("sharedRedemptionUrl", createForm);
  const watchedEditSharedUrl = Form.useWatch("sharedRedemptionUrl", editForm);
  const watchedCreateCurrency = Form.useWatch("currency", createForm);
  const watchedEditCurrency = Form.useWatch("currency", editForm);
  const createUsesCsvPool = isPoolBackedVoucher({
    consumptionType: watchedCreateConsumptionType,
    urlMode: watchedCreateUrlMode,
  });
  const editUsesCsvPool = isPoolBackedVoucher({
    consumptionType: watchedEditConsumptionType,
    urlMode: watchedEditUrlMode,
  });
  const watchedCreateVoucherType = Form.useWatch("voucherType", createForm);
  const watchedEditVoucherType = Form.useWatch("voucherType", editForm);
  // Drives the "how does this expire" summary in each Validity Period section.
  const watchedCreateRedemptionDays = Form.useWatch(
    "redemptionValidityDays",
    createForm,
  );
  const watchedEditRedemptionDays = Form.useWatch(
    "redemptionValidityDays",
    editForm,
  );
  // Relabels the date pickers: with a relative window they are the catalogue
  // shelf window, not the member's expiry.
  const hasEditRedemptionWindow = Number(watchedEditRedemptionDays) >= 1;
  const hasCreateRedemptionWindow = Number(watchedCreateRedemptionDays) >= 1;

  // Imported coupon-code (CSV) pool state for the edit modal
  const [importCodesStats, setImportCodesStats] = useState(null);
  const [importCodesUploading, setImportCodesUploading] = useState(false);

  // Coupon-code CSV selected during voucher creation (uploaded right after the
  // voucher is created, so admins never need to revisit the edit screen).
  const [createImportFile, setCreateImportFile] = useState(null);

  // Tier preview while editing (synced on open + onChange of cost/markup/base)
  const [editBasePrice, setEditBasePrice] = useState(0);
  const [editCostPrice, setEditCostPrice] = useState(0);
  const [editMarkupPercent, setEditMarkupPercent] = useState(null);

  const resolveMarkupFromFormValue = (raw, voucher, rates) => {
    if (raw !== null && raw !== undefined && raw !== "") {
      const n = Number(raw);
      if (Number.isFinite(n) && n >= 0) return n;
    }
    return resolveEffectiveMarkupPercent(voucher, rates);
  };

  // Selected voucher
  const [selectedVoucher, setSelectedVoucher] = useState(null);
  const [voucherCodes, setVoucherCodes] = useState([]);
  const [codesLoading, setCodesLoading] = useState(false);
  const [codesPagination, setCodesPagination] = useState(null);
  const [codesStats, setCodesStats] = useState(null);
  const [codesFilter, setCodesFilter] = useState({
    status: "all",
    page: 1,
    pageSize: 10,
  });
  const [exportModalVisible, setExportModalVisible] = useState(false);
  const [exportStatusOptions, setExportStatusOptions] = useState(["all"]);

  // Filters
  const [filters, setFilters] = useState({
    search: "",
    category: undefined,
    status: undefined,
    country: undefined,
    brand: undefined,
    provider: undefined,
    minCostBaseDiscount: undefined,
    maxCostBaseDiscount: undefined,
  });

  // Temporary filters for Apply functionality
  const [tempFilters, setTempFilters] = useState({
    category: [],
    country: [],
    brand: [],
  });

  // Filter options - dynamic from database
  const [categories, setCategories] = useState([]);
  const [countries, setCountries] = useState([]);
  const [brands, setBrands] = useState([]);
  const [providers, setProviders] = useState([]);

  const [pricingTiers, setPricingTiers] = useState([]);

  // Provider sync states
  const [syncLoading, setSyncLoading] = useState(false);
  const [bulkOnlineLoading, setBulkOnlineLoading] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);
  const [bulkMarkupModalVisible, setBulkMarkupModalVisible] = useState(false);
  const [bulkRedemptionModalVisible, setBulkRedemptionModalVisible] = useState(false);
  const [bulkRedemptionLoading, setBulkRedemptionLoading] = useState(false);
  const [bulkMarkupLoading, setBulkMarkupLoading] = useState(false);
  const [zhongminScriptKey, setZhongminScriptKey] = useState(null);
  const [syncStatus, setSyncStatus] = useState({});
  const [nyyhXlsxList, setNyyhXlsxList] = useState([]);
  const [nyyhImageList, setNyyhImageList] = useState([]);
  const [zhongminNyyhUploading, setZhongminNyyhUploading] = useState(false);
  const [zhongminNyyhUploadMode, setZhongminNyyhUploadMode] = useState(null);

  // Provider settlement-markup states. View mode shows the persisted percentages;
  // Edit mode allows draft changes that only persist after Save.
  // Stored value is the markup percent (e.g. 10 → +10%).
  const PROVIDER_RATE_NAMES = ["gowabi", "giftee", "zhongmin", "iak"];
  const [providerRates, setProviderRates] = useState({});
  const [providerRatesLoading, setProviderRatesLoading] = useState(false);
  const [providerRateMode, setProviderRateMode] = useState("view"); // 'view' | 'edit'
  const [providerRateDraft, setProviderRateDraft] = useState({});
  const [providerRateSaving, setProviderRateSaving] = useState(false);

  // Image upload and crop states
  const [imageFile, setImageFile] = useState(null);
  const [imageSrc, setImageSrc] = useState(null);
  const [crop, setCrop] = useState({
    unit: "%",
    width: 90,
    height: 67.5, // 90 * (3/4) to maintain 4:3 aspect ratio
    x: 5,
    y: 16.25,
  });
  const [completedCrop, setCompletedCrop] = useState(null);
  const [croppedImageUrl, setCroppedImageUrl] = useState(null);
  const [cropModalVisible, setCropModalVisible] = useState(false);
  const imgRef = useRef(null);
  /** Latest edit form values when entering preview (modal unmounts; restore on return). */
  const editFormDraftRef = useRef(null);
  const createFormDraftRef = useRef(null);

  // 1. Edit image related state
  const [editImageFile, setEditImageFile] = useState(null);
  const [editImageSrc, setEditImageSrc] = useState(null);
  const [editCroppedImageUrl, setEditCroppedImageUrl] = useState(null);
  const [editCrop, setEditCrop] = useState({
    unit: "%",
    width: 90,
    height: 67.5,
    x: 5,
    y: 16.25,
  });
  const [editCompletedCrop, setEditCompletedCrop] = useState(null);
  const [editCropModalVisible, setEditCropModalVisible] = useState(false);
  const editImgRef = useRef(null);

  useEffect(() => {
    // Add debounce to prevent excessive API calls
    const timeoutId = setTimeout(() => {
      fetchVouchers();
      fetchPricingTiers();
      fetchFilterOptions();
    }, 300);

    return () => clearTimeout(timeoutId);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pagination.current, pagination.pageSize, filters]);

  // Handle navigation state from Dashboard
  useEffect(() => {
    const state = location.state;
    if (!state) return;
    if (state.openCreateModal) {
      createFormDraftRef.current = null;
      setCreateModalVisible(true);
    }
    if (state.filterLowStock) {
      // Best-effort low-stock deeplink: narrow to active vouchers so the user
      // can scan the Sales Info column for low remaining capacity. A
      // first-class lowStock filter can be wired here once the backend
      // exposes one.
      setFilters((prev) => ({ ...prev, status: "online" }));
      setPagination((prev) => ({ ...prev, current: 1 }));
      message.info("Showing online vouchers — check the Stock column for low stock");
    }
    if (state.openCreateModal || state.filterLowStock) {
      navigate("/admin/vouchers", { replace: true });
    }
  }, [location.state, navigate]);

  // Handle apply category filters
  const handleApplyCategory = () => {
    setFilters((prev) => ({
      ...prev,
      category:
        tempFilters.category.length > 0 ? tempFilters.category : undefined,
    }));
    setPagination((prev) => ({ ...prev, current: 1 }));
  };

  // Handle apply country filters
  const handleApplyCountry = () => {
    setFilters((prev) => ({
      ...prev,
      country: tempFilters.country.length > 0 ? tempFilters.country : undefined,
    }));
    setPagination((prev) => ({ ...prev, current: 1 }));
  };

  // Handle apply brand filters
  const handleApplyBrand = () => {
    setFilters((prev) => ({
      ...prev,
      brand: tempFilters.brand.length > 0 ? tempFilters.brand : undefined,
    }));
    setPagination((prev) => ({ ...prev, current: 1 }));
  };

  // Handle reset filters
  const handleResetFilters = () => {
    setTempFilters({ category: [], country: [], brand: [] });
    setFilters({
      search: "",
      category: undefined,
      status: undefined,
      country: undefined,
      brand: undefined,
      provider: undefined,
      minCostBaseDiscount: undefined,
      maxCostBaseDiscount: undefined,
    });
    setPagination((prev) => ({ ...prev, current: 1 }));
  };

  // Image handling methods
  const handleImageChange = (info) => {
    const { file } = info;
    if (file.status === "removed") {
      setImageFile(null);
      setImageSrc(null);
      setCroppedImageUrl(null);
      return;
    }

    const rawFile = file.originFileObj || file;
    const imageError = getImageValidationError(rawFile);
    if (imageError) {
      message.error(imageError);
      return;
    }

    const reader = new FileReader();
    reader.onload = (e) => {
      setImageSrc(e.target.result);
      setCropModalVisible(true);
    };
    reader.readAsDataURL(file.originFileObj || file);
    setImageFile(file.originFileObj || file);
  };

  const handleCropComplete = async () => {
    if (!completedCrop || !imgRef.current) return;

    try {
      const croppedImage = await getCroppedImg(
        imgRef.current,
        completedCrop,
        imageFile.name,
      );

      // Create object URL for preview. Releasing the previous one prevents
      // a slow leak when the user re-crops the same upload several times.
      const imageUrl = URL.createObjectURL(croppedImage);
      setCroppedImageUrl((prev) => {
        if (prev) URL.revokeObjectURL(prev);
        return imageUrl;
      });
      setImageFile(croppedImage);
      setCropModalVisible(false);

      message.success("Image cropped successfully");
    } catch (error) {
      console.error("Error cropping image:", error);
      message.error("Failed to crop image");
    }
  };

  const handleCropCancel = () => {
    setCropModalVisible(false);
    setImageSrc(null);
    setImageFile(null);
  };

  const uploadProps = {
    beforeUpload: () => false, // Prevent auto upload
    onChange: handleImageChange,
    showUploadList: false,
    accept: ACCEPTED_IMAGE_ACCEPT,
    maxCount: 1,
  };

  // 2. Edit image upload handler
  const handleEditImageChange = (info) => {
    const { file } = info;
    if (file.status === "removed") {
      setEditImageFile(null);
      setEditImageSrc(null);
      setEditCroppedImageUrl(null);
      return;
    }
    const rawFile = file.originFileObj || file;
    const imageError = getImageValidationError(rawFile);
    if (imageError) {
      message.error(imageError);
      return;
    }
    const reader = new FileReader();
    reader.onload = (e) => {
      setEditImageSrc(e.target.result);
      setEditCropModalVisible(true);
    };
    reader.readAsDataURL(file.originFileObj || file);
    setEditImageFile(file.originFileObj || file);
  };

  const handleEditCropComplete = async () => {
    if (!editCompletedCrop || !editImgRef.current) return;
    try {
      const croppedImage = await getCroppedImg(
        editImgRef.current,
        editCompletedCrop,
        editImageFile.name,
      );
      const imageUrl = URL.createObjectURL(croppedImage);
      setEditCroppedImageUrl((prev) => {
        if (prev) URL.revokeObjectURL(prev);
        return imageUrl;
      });
      setEditImageFile(croppedImage);
      setEditCropModalVisible(false);
      message.success("Image cropped successfully");
    } catch (error) {
      console.error("Error cropping image:", error);
      message.error("Failed to crop image");
    }
  };
  const handleEditCropCancel = () => {
    setEditCropModalVisible(false);
    setEditImageSrc(null);
    setEditImageFile(null);
  };
  const editUploadProps = {
    beforeUpload: () => false,
    onChange: handleEditImageChange,
    showUploadList: false,
    accept: ACCEPTED_IMAGE_ACCEPT,
    maxCount: 1,
  };

  // ─── Coupon-code CSV client-side validation ──────────────────────────────
  // Read a File/Blob as UTF-8 text.
  const readFileAsText = (file) =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e) => resolve(e.target?.result ?? "");
      reader.onerror = () =>
        reject(reader.error || new Error("Failed to read file"));
      reader.readAsText(file);
    });

  // Header cells the backend importer skips on line 0. Mirrors
  // VOUCHER_CODE_CSV_HEADER_RE in backend/admin/admin.routes.js.
  const CSV_HEADER_RE =
    /^(code|codes|voucher|id|voucher_code|coupon|coupon_code|url|urls|link|links|redemption_url|qr|qrcode|qr_code|payload|content)$/i;

  // Count valid, unique values the same way the backend importer does: strip
  // BOM, drop an optional header row, ignore blank lines, apply the per-type
  // value check, de-dup by value. Per VIO's format the whole line is the
  // value — never split on commas.
  const countCsvCodes = (text, consumptionType) => {
    const lines = text
      .replace(/^\uFEFF/, "")
      .split(/\r\n|\n|\r/)
      .map((l) => l.trim())
      .filter((l) => l.length > 0);
    const seen = new Set();
    lines.forEach((line, index) => {
      const value = line.replace(/^"(.*)"$/, "$1").trim();
      if (index === 0 && CSV_HEADER_RE.test(value)) {
        return;
      }
      if (!value) return;
      // URL vouchers hand out one-time links; anything that isn't a link is
      // rejected by the backend too, so don't count it towards the quantity.
      if (consumptionType === "URL" && !/^https?:\/\//i.test(value)) return;
      seen.add(value);
    });
    return seen.size;
  };

  // Validate an uploaded pool CSV before accepting it: it must be a non-empty,
  // plain-text (non-binary) .csv with at least one usable value for the type.
  // Resolves to { valid, reason, codeCount }.
  const validatePoolCsv = async (file, consumptionType) => {
    const copy = getConsumptionCsvCopy(consumptionType);
    if (!file) return { valid: false, reason: "No file selected", codeCount: 0 };
    if (!/\.csv$/i.test(file.name || "")) {
      return { valid: false, reason: "Please select a .csv file", codeCount: 0 };
    }
    if (file.size === 0) {
      return { valid: false, reason: "The CSV file is empty", codeCount: 0 };
    }
    let text = "";
    try {
      text = await readFileAsText(file);
    } catch (e) {
      return {
        valid: false,
        reason: "Could not read the CSV file",
        codeCount: 0,
      };
    }
    if (!text.trim()) {
      return { valid: false, reason: "The CSV file is empty", codeCount: 0 };
    }
    // Binary detection: a null byte or a high ratio of Unicode replacement
    // characters means this is not a plain-text CSV (e.g. an .xlsx/.pdf/image
    // renamed to .csv).
    const replacementCount = (text.match(/\uFFFD/g) || []).length;
    if (text.includes(String.fromCharCode(0)) || replacementCount / text.length > 0.01) {
      return {
        valid: false,
        reason: "The file is not a valid text/CSV file",
        codeCount: 0,
      };
    }
    const codeCount = countCsvCodes(text, consumptionType);
    if (codeCount === 0) {
      return {
        valid: false,
        reason: `The CSV file has no valid ${copy.plural}`,
        codeCount: 0,
      };
    }
    return { valid: true, reason: "", codeCount };
  };

  // Validate the pool CSV chosen during voucher creation, then auto-sync the
  // Total Quantity field from the detected value count.
  const handleSelectCreateCsv = async (file) => {
    const consumptionType = createForm.getFieldValue("consumptionType");
    const copy = getConsumptionCsvCopy(consumptionType);
    const result = await validatePoolCsv(file, consumptionType);
    if (!result.valid) {
      message.error(result.reason);
      setCreateImportFile(null);
      return;
    }
    setCreateImportFile(file);
    createForm.setFieldsValue({ totalQuantity: result.codeCount });
    message.success(`Detected ${result.codeCount} ${copy.singular}(s) in the CSV`);
  };

  const getProcessedFilters = useCallback((sourceFilters = filters) => (
    Object.entries(sourceFilters).reduce((acc, [key, value]) => {
      if (value !== undefined && value !== null && value !== "") {
        if (Array.isArray(value) && value.length > 0) {
          // Keep arrays as arrays for proper backend $in handling.
          acc[key] = value;
        } else if (!Array.isArray(value)) {
          acc[key] = value;
        }
      }
      return acc;
    }, {})
  ), [filters]);

  const getBulkOnlineFilterSummary = () => {
    const summary = [];
    if (filters.search) summary.push(`Search: "${filters.search}"`);
    if (filters.category?.length) {
      summary.push(`Category: ${filters.category.join(", ")}`);
    }
    if (filters.country?.length) {
      summary.push(`Country: ${filters.country.map(getCountryDisplayName).join(", ")}`);
    }
    if (filters.brand?.length) summary.push(`Brand: ${filters.brand.join(", ")}`);
    if (filters.provider) {
      summary.push(`Provider: ${formatVoucherProviderLabel(filters.provider)}`);
    }
    if (
      filters.minCostBaseDiscount !== undefined ||
      filters.maxCostBaseDiscount !== undefined
    ) {
      summary.push(
        `Cost discount: ${filters.minCostBaseDiscount ?? COST_DISCOUNT_SLIDER_MIN}% - ${filters.maxCostBaseDiscount ?? COST_DISCOUNT_SLIDER_MAX}%`,
      );
    }
    return summary.length > 0 ? summary.join("; ") : "All vouchers";
  };

  const hasActiveBulkMarkupFilters = () =>
    Boolean(
      filters.search ||
        filters.status ||
        filters.provider ||
        filters.category?.length ||
        filters.country?.length ||
        filters.brand?.length ||
        filters.minCostBaseDiscount !== undefined ||
        filters.maxCostBaseDiscount !== undefined,
    );

  const formatMarkupPercent = (value) =>
    `${Number(value).toLocaleString(undefined, {
      maximumFractionDigits: 4,
    })}%`;

  const costDiscountFilterActive =
    filters.minCostBaseDiscount !== undefined ||
    filters.maxCostBaseDiscount !== undefined;

  const costDiscountSliderValue = [
    filters.minCostBaseDiscount ?? COST_DISCOUNT_SLIDER_MIN,
    filters.maxCostBaseDiscount ?? COST_DISCOUNT_SLIDER_MAX,
  ];

  const formatCostDiscountRange = (min, max) =>
    `${formatMarkupPercent(min)} - ${formatMarkupPercent(max)}`;

  const getCostDiscountFilterLabel = () =>
    costDiscountFilterActive
      ? formatCostDiscountRange(
          filters.minCostBaseDiscount ?? COST_DISCOUNT_SLIDER_MIN,
          filters.maxCostBaseDiscount ?? COST_DISCOUNT_SLIDER_MAX,
        )
      : "All discount levels";

  const applyCostDiscountRange = (min, max) => {
    // Both handles at the extremes means "no restriction", so drop the params
    // instead of sending a range that matches everything.
    const shouldClear =
      Number(min) <= COST_DISCOUNT_SLIDER_MIN &&
      Number(max) >= COST_DISCOUNT_SLIDER_MAX;
    setFilters((prev) => ({
      ...prev,
      minCostBaseDiscount: shouldClear ? undefined : Number(min),
      maxCostBaseDiscount: shouldClear ? undefined : Number(max),
    }));
    setPagination((prev) => ({ ...prev, current: 1 }));
  };

  const handleCostDiscountSliderChange = (value) => {
    const [min, max] = Array.isArray(value)
      ? value
      : [COST_DISCOUNT_SLIDER_MIN, COST_DISCOUNT_SLIDER_MAX];
    applyCostDiscountRange(min, max);
  };

  // Typed bound from the InputNumber pair. Clearing an input falls back to that
  // end's extreme, and the other bound is pushed along so min never exceeds max.
  const handleCostDiscountBoundChange = (bound, rawValue) => {
    const [currentMin, currentMax] = costDiscountSliderValue;
    const fallback =
      bound === "min" ? COST_DISCOUNT_SLIDER_MIN : COST_DISCOUNT_SLIDER_MAX;
    const parsed = Number(rawValue);
    const next = Number.isFinite(parsed)
      ? Math.min(
          COST_DISCOUNT_SLIDER_MAX,
          Math.max(COST_DISCOUNT_SLIDER_MIN, parsed),
        )
      : fallback;

    if (bound === "min") {
      applyCostDiscountRange(next, Math.max(next, currentMax));
    } else {
      applyCostDiscountRange(Math.min(next, currentMin), next);
    }
  };

  const clearCostDiscountFilter = () => {
    setFilters((prev) => ({
      ...prev,
      minCostBaseDiscount: undefined,
      maxCostBaseDiscount: undefined,
    }));
    setPagination((prev) => ({ ...prev, current: 1 }));
  };

  const getBulkMarkupFilterSummary = () => {
    const summary = [];
    if (filters.search) summary.push(`Search: "${filters.search}"`);
    if (filters.category?.length) {
      summary.push(`Category: ${filters.category.join(", ")}`);
    }
    if (filters.status) summary.push(`Status: ${filters.status}`);
    if (filters.country?.length) {
      summary.push(`Country: ${filters.country.map(getCountryDisplayName).join(", ")}`);
    }
    if (filters.brand?.length) summary.push(`Brand: ${filters.brand.join(", ")}`);
    if (filters.provider) {
      summary.push(`Provider: ${formatVoucherProviderLabel(filters.provider)}`);
    }
    if (costDiscountFilterActive) {
      summary.push(`Cost discount: ${getCostDiscountFilterLabel()}`);
    }
    return summary.length > 0 ? summary.join("; ") : "All active vouchers";
  };

  const getVisibleMarkupPreview = () => {
    const values = vouchers
      .map((voucher) => resolveEffectiveMarkupPercent(voucher, providerRates))
      .filter((value) => Number.isFinite(Number(value)));

    if (values.length === 0) {
      return {
        visibleCount: vouchers.length,
        currentRange: "No current mark up on this page",
      };
    }

    const min = Math.min(...values);
    const max = Math.max(...values);
    return {
      visibleCount: vouchers.length,
      currentRange:
        min === max
          ? formatMarkupPercent(min)
          : `${formatMarkupPercent(min)} - ${formatMarkupPercent(max)}`,
    };
  };

  const getVisibleCostBaseDiscountPreview = () => {
    const values = vouchers
      .map(getCostBaseDiscountPercent)
      .filter((value) => Number.isFinite(Number(value)));

    if (values.length === 0) {
      return "No cost discount on this page";
    }

    const min = Math.min(...values);
    const max = Math.max(...values);
    return min === max
      ? formatMarkupPercent(min)
      : `${formatMarkupPercent(min)} - ${formatMarkupPercent(max)}`;
  };

  // Fetch vouchers with debounce to prevent excessive API calls
  const fetchVouchers = useCallback(
    async (preventLoading = false) => {
      try {
        if (!preventLoading) {
          setLoading(true);
        }

        const processedFilters = getProcessedFilters();

        const params = {
          page: pagination.current,
          limit: pagination.pageSize,
          ...processedFilters,
        };

        // Use paramsSerializer to properly handle arrays
        const response = await axios.get("/vouchers/admin/all", {
          params,
          paramsSerializer: {
            indexes: null, // This ensures arrays are serialized as repeated parameters
          },
        });

        // Console log the full API response

        const payload = response.data?.data ?? response.data ?? {};
        setVouchers(payload.vouchers || []);

        setPagination((prev) => ({
          ...prev,
          total:
            payload.pagination?.totalCount ??
            payload.pagination?.total ??
            payload.pagination?.totalItems ??
            0,
        }));
      } catch (error) {
        console.error("Fetch vouchers error:", error);
        showApiError(error, "Failed to load vouchers");
      } finally {
        if (!preventLoading) {
          setLoading(false);
        }
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [getProcessedFilters, pagination.current, pagination.pageSize],
  );

  // Fetch pricing tiers
  const fetchPricingTiers = async () => {
    try {
      const response = await axios.get("/admin/pricing-tiers");
      const rawTiers = Array.isArray(response.data) ? response.data : [];
      setPricingTiers(mergePricingTiersForDisplay(rawTiers));
    } catch (error) {
      console.error("Fetch pricing tiers error:", error);
      // Don't show error message as this is not critical
    }
  };

  // Country code to display name mapping
  const getCountryDisplayName = (countryCode) => {
    const countryMap = {
      // ISO alpha-2 codes (aligned with VIO — current source of truth)
      CN: "China",
      FR: "France",
      HK: "Hong Kong",
      ID: "Indonesia",
      MY: "Malaysia",
      SG: "Singapore",
      TH: "Thailand",
      US: "United States",
      JP: "Japan",
      KR: "South Korea",
      VN: "Vietnam",
      PH: "Philippines",
      AU: "Australia",
      GB: "United Kingdom",
      // Legacy lowercase keys (kept for vouchers created before alignment)
      global: "Global",
      usa: "United States",
      canada: "Canada",
      uk: "United Kingdom",
      germany: "Germany",
      france: "France",
      spain: "Spain",
      italy: "Italy",
      netherlands: "Netherlands",
      australia: "Australia",
      japan: "Japan",
      south_korea: "South Korea",
      singapore: "Singapore",
      hong_kong: "Hong Kong",
      china: "China",
      india: "India",
      brazil: "Brazil",
      mexico: "Mexico",
      thailand: "Thailand",
      malaysia: "Malaysia",
      indonesia: "Indonesia",
      philippines: "Philippines",
      vietnam: "Vietnam",
      taiwan: "Taiwan",
      new_zealand: "New Zealand",
      switzerland: "Switzerland",
      sweden: "Sweden",
      norway: "Norway",
      denmark: "Denmark",
      finland: "Finland",
      belgium: "Belgium",
      austria: "Austria",
      ireland: "Ireland",
      portugal: "Portugal",
      greece: "Greece",
      poland: "Poland",
      czech_republic: "Czech Republic",
      hungary: "Hungary",
      russia: "Russia",
      turkey: "Turkey",
      south_africa: "South Africa",
      egypt: "Egypt",
      nigeria: "Nigeria",
      kenya: "Kenya",
      morocco: "Morocco",
      tunisia: "Tunisia",
      algeria: "Algeria",
      ghana: "Ghana",
      ethiopia: "Ethiopia",
      uganda: "Uganda",
      tanzania: "Tanzania",
      zimbabwe: "Zimbabwe",
      botswana: "Botswana",
      namibia: "Namibia",
      zambia: "Zambia",
      mozambique: "Mozambique",
      angola: "Angola",
      cameroon: "Cameroon",
      cote_divoire: "Côte d'Ivoire",
      senegal: "Senegal",
      mali: "Mali",
      burkina_faso: "Burkina Faso",
      niger: "Niger",
      chad: "Chad",
      central_african_republic: "Central African Republic",
      equatorial_guinea: "Equatorial Guinea",
      gabon: "Gabon",
      congo: "Congo",
      democratic_republic_congo: "Democratic Republic of Congo",
      rwanda: "Rwanda",
      burundi: "Burundi",
      somalia: "Somalia",
      djibouti: "Djibouti",
      eritrea: "Eritrea",
      sudan: "Sudan",
      south_sudan: "South Sudan",
      libya: "Libya",
      mauritania: "Mauritania",
      other: "Other",
    };
    return countryMap[countryCode] || countryCode;
  };

  // Human-readable category labels, kept identical to the create/edit form
  // <Select> options so the same name shows everywhere (table, details, edit).
  const CATEGORY_LABELS = {
    food_beverage: "Food & Beverage",
    travel_hospitality: "Travel & Hospitality",
    wellness: "Wellness",
    health: "Health",
    leisure_entertainment: "Leisure & Entertainment",
    lifestyle_services: "Lifestyle & Services",
    egift: "eGift",
    others: "Others",
  };
  const getCategoryLabel = (category) =>
    CATEGORY_LABELS[category] ||
    (category ? category.replace(/_/g, " ") : "");

  // Fetch filter options from database
  const fetchFilterOptions = async () => {
    try {
      const response = await axios.get("/vouchers/admin/filter-options");
      setCategories(VOUCHER_CATEGORY_OPTIONS.map((option) => option.value));
      setCountries(response.data.data?.countries || []);
      setBrands(response.data.data?.brands || []);
      setProviders(response.data.data?.providers || []);
    } catch (error) {
      console.error("Fetch filter options error:", error);
      setCategories(VOUCHER_CATEGORY_OPTIONS.map((option) => option.value));
      setCountries([]);
      setBrands([]);
      setProviders([]);
    }
  };

  const fetchSyncStatus = async () => {
    try {
      const response = await axios.get("/admin/vouchers/sync-status");
      setSyncStatus(response.data || {});
    } catch (error) {
      console.error("Fetch sync status error:", error);
    }
  };

  const fetchProviderRates = useCallback(async () => {
    setProviderRatesLoading(true);
    try {
      const response = await axios.get("/admin/vouchers/provider-rates");
      setProviderRates(response.data?.rates || {});
    } catch (error) {
      console.error("Fetch provider rates error:", error);
      showApiError(error, "Failed to load provider rates");
    } finally {
      setProviderRatesLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchSyncStatus();
    fetchProviderRates();
  }, [fetchProviderRates]);

  const enterProviderRateEditMode = () => {
    const draft = {};
    for (const name of PROVIDER_RATE_NAMES) {
      const current = providerRates?.[name]?.markupPercent;
      draft[name] = Number.isFinite(Number(current)) ? Number(current) : 0;
    }
    setProviderRateDraft(draft);
    setProviderRateMode("edit");
  };

  const cancelProviderRateEdit = () => {
    setProviderRateDraft({});
    setProviderRateMode("view");
  };

  const handleProviderRateDraftChange = (providerName, value) => {
    setProviderRateDraft((prev) => ({
      ...prev,
      [providerName]: value,
    }));
  };

  const saveProviderRates = async () => {
    const entries = [];
    for (const name of PROVIDER_RATE_NAMES) {
      const raw = providerRateDraft[name];
      const num = Number(raw);
      if (!Number.isFinite(num) || num < 0) {
        message.error(`Invalid mark up for ${name}`);
        return;
      }
      entries.push({ providerName: name, markupPercent: num });
    }

    setProviderRateSaving(true);
    try {
      const response = await axios.put("/admin/vouchers/provider-rates", {
        rates: entries,
      });
      setProviderRates(response.data?.rates || {});
      setProviderRateMode("view");
      setProviderRateDraft({});
      message.success("Provider mark up saved");
    } catch (error) {
      console.error("Save provider rates error:", error);
      showApiError(error, "Failed to save provider mark up");
    } finally {
      setProviderRateSaving(false);
    }
  };

  const handleSyncProvider = async (providerName) => {
    const target = providerName;
    if (!target) {
      message.warning("Please select a provider");
      return;
    }
    setSyncLoading(true);
    try {
      const response = await axios.post("/admin/vouchers/sync-provider", {
        providerName: target,
      });
      const data = response.data;
      message.success(
        `${target} sync completed: ${data.added} added, ${data.updated} updated, ${data.removed} removed`
      );
      if (data.errors && data.errors.length > 0) {
        const preview = data.errors.slice(0, 2).join(" | ");
        message.warning(
          `${data.errors.length} errors during sync${preview ? `: ${preview}` : ""}`,
          6
        );
      }
      fetchSyncStatus();
      fetchVouchers();
      fetchFilterOptions();
    } catch (error) {
      showApiError(error, `Sync failed: ${error.message}`);
    } finally {
      setSyncLoading(false);
    }
  };

  const runZhongminProviderScript = async (scriptKey) => {
    setZhongminScriptKey(scriptKey);
    try {
      const response = await axios.post(
        "/admin/vouchers/zhongmin-run-script",
        { script: scriptKey },
        { timeout: ZHONGMIN_SCRIPT_HTTP_TIMEOUT_MS }
      );
      const d = response.data;
      if (scriptKey === "all") {
        message.success(
          `Run scripts: ${d.added ?? 0} added, ${d.updated ?? 0} updated, ${d.removed ?? 0} removed`
        );
      } else if (scriptKey === "sync-to-db") {
        message.success(
          `Sync to DB: ${d.added ?? 0} added, ${d.updated ?? 0} updated, ${d.removed ?? 0} removed`
        );
      } else {
        message.success(
          `DB → Frontend snapshot: ${Array.isArray(d.vouchers) ? d.vouchers.length : 0} vouchers`
        );
      }
      if (d.errors && d.errors.length > 0) {
        message.warning(
          `${d.errors.length} error(s): ${d.errors.slice(0, 2).join(" | ")}`,
          6
        );
      }
      fetchSyncStatus();
      fetchVouchers();
    } catch (error) {
      const apiMsg = error.response?.data?.message;
      const raw = apiMsg || error.message || "Script failed";
      const isTimeout =
        error.code === "ECONNABORTED" ||
        /timeout/i.test(String(error.message || ""));
      if (isTimeout && !apiMsg) {
        message.error(
          "Timed out waiting for Zhongmin script (can take several minutes). Increase proxy/gateway read timeout (e.g. nginx proxy_read_timeout) and ensure backend can reach the provider service."
        );
      } else {
        showApiError(error, raw);
      }
    } finally {
      setZhongminScriptKey(null);
    }
  };

  const handleRunScriptsClick = () => {
    Modal.confirm({
      title: "Run Zhongmin scripts?",
      content:
        "This runs sync-to-db followed by db-to-frontend. It can take several minutes.",
      okText: "Run scripts",
      cancelText: "Cancel",
      width: MODAL_WIDTH.sm,
      onOk: () => runZhongminProviderScript("all"),
    });
  };

  const handleNyyhUploadAndRun = () => {
    const hasSpreadsheet = nyyhXlsxList.length > 0;
    const hasImages = nyyhImageList.length > 0;
    if (!hasSpreadsheet && !hasImages) {
      message.warning("Please select a .xlsx spreadsheet file or one or more images.");
      return;
    }

    const xlsx = hasSpreadsheet ? nyyhXlsxList[0]?.originFileObj : null;
    if (hasSpreadsheet && !xlsx) {
      message.warning("Please select a .xlsx spreadsheet file.");
      return;
    }

    Modal.confirm({
      title: "Upload NYYH files and run scripts?",
      content: (
        <div className="voucher-admin">
          {hasSpreadsheet && (
            <p className="va-modal-lead">
              This overwrites the Zhongmin NYYH master spreadsheet on the server.
            </p>
          )}
          {hasImages && (
            <p className="va-modal-lead">
              This adds or replaces image files in the Zhongmin NYYH asset folder.
              Files with the same file name are overwritten.
            </p>
          )}
          <p className="va-help">
            After the upload finishes, the scripts start automatically (sync the
            spreadsheet to the database, then refresh the frontend snapshot).
            This can take several minutes.
          </p>
        </div>
      ),
      okText: "Upload and run",
      cancelText: "Cancel",
      width: MODAL_WIDTH.md,
      onOk: async () => {
        setZhongminNyyhUploadMode("upload-run");
        setZhongminNyyhUploading(true);
        try {
          if (hasImages) {
            const imageFd = new FormData();
            nyyhImageList.forEach((item) => {
              const img = item.originFileObj;
              if (img) {
                imageFd.append("images", img, item.name);
              }
            });
            await axios.post(
              "/admin/vouchers/zhongmin-nyyh-images-upload",
              imageFd,
              { timeout: ZHONGMIN_SCRIPT_HTTP_TIMEOUT_MS }
            );
            setNyyhImageList([]);
          }

          if (hasSpreadsheet) {
            const spreadsheetFd = new FormData();
            spreadsheetFd.append("spreadsheet", xlsx, xlsx.name);
            await axios.post("/admin/vouchers/zhongmin-nyyh-upload", spreadsheetFd, {
              timeout: ZHONGMIN_SCRIPT_HTTP_TIMEOUT_MS,
            });
            setNyyhXlsxList([]);
          }

          message.success("Files saved. Running scripts…");
          await runZhongminProviderScript("all");
        } catch (err) {
          const d = err.response?.data;
          if (d?.code === "NYYH_XLSX_HEADER_MISMATCH") {
            const rows = Array.isArray(d.headerMismatches)
              ? d.headerMismatches
              : [];
            const exp = (d.expectedHeaders || []).join(" | ");
            const up = (d.uploadedHeaders || []).join(" | ");
            Modal.error({
              title: "Spreadsheet column headers do not match",
              width: MODAL_WIDTH.lg,
              content: (
                <div className="voucher-admin va-mismatch">
                  <p className="va-modal-lead">
                    Use the <Text strong>correct name</Text> in the first row for
                    each position below. &quot;Column #&quot; is the 1-based index
                    in the header row (1 = first column, 2 = second, etc.).
                  </p>
                  {rows.length > 0 ? (
                    <Table
                      size="small"
                      pagination={false}
                      dataSource={rows.map((m, i) => ({
                        key: i,
                        columnIndex: m.columnIndex,
                        expected: m.expected,
                        uploaded: m.uploaded,
                      }))}
                      columns={[
                        {
                          title: "Column #",
                          dataIndex: "columnIndex",
                          width: 86,
                        },
                        {
                          title: "Correct name (template)",
                          dataIndex: "expected",
                          ellipsis: true,
                        },
                        {
                          title: "Your file (wrong or missing)",
                          dataIndex: "uploaded",
                          ellipsis: true,
                        },
                      ]}
                      scroll={{ y: 260 }}
                    />
                  ) : (
                    <>
                      <div className="va-field-label">
                        Template (on server) headers
                      </div>
                      <pre>{exp || "(empty)"}</pre>
                      <div className="va-field-label">Your file headers</div>
                      <pre>{up || "(empty)"}</pre>
                    </>
                  )}
                </div>
              ),
            });
          }
          throw err;
        } finally {
          setZhongminNyyhUploading(false);
          setZhongminNyyhUploadMode(null);
        }
      },
    });
  };

  const handleSyncAll = async () => {
    setSyncLoading(true);
    try {
      const providers = ["gowabi", "giftee", "zhongmin", "iak"];
      const results = [];
      for (const p of providers) {
        try {
          const response = await axios.post("/admin/vouchers/sync-provider", {
            providerName: p,
          });
          results.push({ provider: p, ...response.data });
        } catch (error) {
          results.push({
            provider: p,
            error: error.response?.data?.message || error.message,
          });
        }
      }
      const totalAdded = results.reduce((s, r) => s + (r.added || 0), 0);
      const totalUpdated = results.reduce((s, r) => s + (r.updated || 0), 0);
      const totalRemoved = results.reduce((s, r) => s + (r.removed || 0), 0);
      const providerErrorCount = results.filter((r) => r.error).length;
      const syncItemErrorCount = results.reduce(
        (sum, r) => sum + (Array.isArray(r.errors) ? r.errors.length : 0),
        0
      );

      const summary = `Synced: ${totalAdded} added, ${totalUpdated} updated, ${totalRemoved} removed`;
      if (providerErrorCount === providers.length) {
        message.error(`All ${providers.length} providers failed to sync`);
      } else if (providerErrorCount > 0 || syncItemErrorCount > 0) {
        message.warning(
          `${summary} — ${providerErrorCount} provider error(s), ${syncItemErrorCount} item error(s)`
        );
      } else {
        message.success(`All providers synced. ${summary}`);
      }
      // Refresh data; failures here must NOT leave the Sync All button stuck
      // in a permanent loading state, so they are caught individually.
      try { await fetchSyncStatus(); } catch (e) { console.error(e); }
      try { await fetchVouchers(); } catch (e) { console.error(e); }
      try { await fetchFilterOptions(); } catch (e) { console.error(e); }
    } catch (error) {
      console.error("Sync All failed:", error);
      showApiError(error, "Failed to sync providers");
    } finally {
      setSyncLoading(false);
    }
  };

  const handleOnlineFilteredOfflineVouchers = async () => {
    setBulkOnlineLoading(true);
    try {
      const response = await axios.put("/vouchers/admin/filtered-offline-online", {
        filters: getProcessedFilters(),
      });
      const modifiedCount = response.data?.data?.modifiedCount ?? 0;
      const matchedCount = response.data?.data?.matchedCount ?? modifiedCount;

      if (modifiedCount > 0) {
        message.success(`${modifiedCount} offline voucher${modifiedCount === 1 ? "" : "s"} are now online`);
      } else if (matchedCount > 0) {
        message.info("Matching offline vouchers were already online-ready; no changes were needed");
      } else {
        message.info("No offline vouchers matched the current filters");
      }

      await fetchVouchers(true);
      fetchFilterOptions();
    } catch (error) {
      console.error("Bulk online filtered offline vouchers failed:", error);
      showApiError(error, "Failed to bring filtered offline vouchers online");
    } finally {
      setBulkOnlineLoading(false);
    }
  };

  const handleBulkMarkupSubmit = async (values) => {
    const nextMarkupPercent = Number(values.markupPercent);
    if (!Number.isFinite(nextMarkupPercent) || nextMarkupPercent < 0) {
      message.error("Please enter a valid mark up");
      return;
    }

    setBulkMarkupLoading(true);
    try {
      const response = await axios.put("/vouchers/admin/bulk-markup", {
        filters: getProcessedFilters(),
        markupPercent: nextMarkupPercent,
        reason: String(values.reason || "").trim(),
      });

      const result = response.data?.data || {};
      const modifiedCount = result.modifiedCount ?? 0;
      const matchedCount = result.matchedCount ?? modifiedCount;

      if (modifiedCount > 0) {
        message.success(
          `${modifiedCount} voucher${modifiedCount === 1 ? "" : "s"} set to ${formatMarkupPercent(nextMarkupPercent)} mark up`,
        );
      } else if (matchedCount > 0) {
        message.info("Matching vouchers already use this mark up");
      } else {
        message.info("No vouchers matched the current filters");
      }

      setBulkMarkupModalVisible(false);
      bulkMarkupForm.resetFields();
      await fetchVouchers(true);
    } catch (error) {
      console.error("Bulk update filtered voucher markup failed:", error);
      showApiError(error, "Failed to update filtered voucher mark up");
    } finally {
      setBulkMarkupLoading(false);
    }
  };

  const handleBulkRedemptionSubmit = async (values) => {
    const raw = values.redemptionValidityDays;
    const clearing = raw === undefined || raw === null || raw === "";
    if (!clearing && (!Number.isInteger(Number(raw)) || Number(raw) < 1)) {
      message.error("Please enter a whole number of days, or leave it empty to clear");
      return;
    }

    setBulkRedemptionLoading(true);
    try {
      const response = await axios.put("/vouchers/admin/bulk-redemption-validity", {
        filters: getProcessedFilters(),
        redemptionValidityDays: clearing ? null : Number(raw),
        onlyUnset: values.onlyUnset !== false,
      });

      const result = response.data?.data || {};
      const modifiedCount = result.modifiedCount ?? 0;
      const matchedCount = result.matchedCount ?? modifiedCount;

      if (modifiedCount > 0) {
        message.success(
          clearing
            ? `Cleared the redemption window on ${modifiedCount} voucher${modifiedCount === 1 ? "" : "s"}`
            : `${modifiedCount} voucher${modifiedCount === 1 ? "" : "s"} set to ${raw} days from purchase`,
        );
      } else if (matchedCount > 0) {
        message.info("Matching vouchers already use this redemption window");
      } else {
        message.info(
          values.onlyUnset !== false
            ? "No vouchers matched the current filters without a redemption window"
            : "No vouchers matched the current filters",
        );
      }

      setBulkRedemptionModalVisible(false);
      bulkRedemptionForm.resetFields();
      await fetchVouchers(true);
    } catch (error) {
      console.error("Bulk update filtered redemption window failed:", error);
      showApiError(error, "Failed to update filtered voucher redemption window");
    } finally {
      setBulkRedemptionLoading(false);
    }
  };

  // Get status display
  // Effective status as plain text, so the table tag and the CSV export never
  // disagree about what a voucher's status is.
  const getStatusLabel = (voucher) => {
    const now = dayjs();
    const startDate = dayjs(voucher.expiryStartDatetime);
    const endDate = dayjs(voucher.expiryEndDatetime);

    if (voucher.voucherStatus === "offline") return "Offline";
    if (voucher.voucherStatus === "expired" || now.isAfter(endDate)) {
      return "Expired";
    }
    if (now.isBefore(startDate)) return "Pending";
    return "Online";
  };

  const STATUS_TONES = {
    Offline: "warning",
    Expired: "danger",
    Pending: "info",
    Online: "success",
  };

  const getStatusDisplay = (voucher) => {
    const label = getStatusLabel(voucher);
    return <StatusTag tone={STATUS_TONES[label]}>{label}</StatusTag>;
  };

  // Strip HTML for plain-text comparison (handles ReactQuill normalization)
  const stripHtml = (html) => {
    if (!html) return "";
    const tmp = document.createElement("DIV");
    tmp.innerHTML = html;
    return (tmp.textContent || tmp.innerText || "").trim();
  };

  // Normalize plain text to avoid false diffs from rich-text formatting
  const normalizePlainText = (html) =>
    stripHtml(html || "")
      .replace(/[\u200B-\u200D\uFEFF]/g, "")
      .replace(/\u00A0/g, " ")
      .replace(/\s+/g, " ")
      .trim();

  // ─── Imported coupon-code (CSV) pool helpers ──────────────────────────────
  const fetchImportCodesStats = useCallback(async (voucherId) => {
    if (!voucherId) return;
    try {
      const res = await axios.get(
        `/admin/vouchers/${voucherId}/codes/import/stats`,
      );
      setImportCodesStats(res.data);
    } catch (error) {
      console.error("Fetch imported code stats error:", error);
      setImportCodesStats(null);
    }
  }, []);

  // Append more values to an existing pool-backed voucher (edit modal).
  // The import endpoint adds new values, de-dupes against the existing pool,
  // and re-syncs totalQuantity — used as antd Upload's beforeUpload (returns
  // false to stop the default request).
  const handleAppendCodesUpload = async (file) => {
    if (!selectedVoucher?._id) {
      message.error("No voucher selected");
      return false;
    }
    const consumptionType =
      editForm.getFieldValue("consumptionType") ||
      selectedVoucher.consumptionType;
    const copy = getConsumptionCsvCopy(consumptionType);
    const validation = await validatePoolCsv(file, consumptionType);
    if (!validation.valid) {
      message.error(validation.reason);
      return false;
    }
    setImportCodesUploading(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      const res = await axios.post(
        `/admin/vouchers/${selectedVoucher._id}/codes/import`,
        formData,
        { headers: { "Content-Type": "multipart/form-data" } },
      );
      const summary = res.data?.summary || {};
      message.success(
        `Added ${summary.inserted ?? 0} ${copy.singular}(s)` +
          (summary.duplicateInFile
            ? `, ${summary.duplicateInFile} duplicate(s) in file`
            : "") +
          (summary.duplicateInDb
            ? `, ${summary.duplicateInDb} already existed`
            : "") +
          (summary.invalidLines
            ? `, ${summary.invalidLines} invalid line(s) skipped`
            : ""),
      );
      await fetchImportCodesStats(selectedVoucher._id);
      if (res.data?.voucher?.totalQuantity != null) {
        editForm.setFieldsValue({
          totalQuantity: res.data.voucher.totalQuantity,
        });
      }
    } catch (error) {
      console.error("Append voucher codes error:", error);
      showApiError(error, `Failed to add ${copy.plural}`);
    } finally {
      setImportCodesUploading(false);
    }
    return false;
  };


  /** Close preview and show the parent create/edit modal again (Quill unmounts while preview is open). */
  // Discount terms, shared by the create and edit modals. Which fields apply
  // is decided by Voucher Type, because that is what VIO's order pricing
  // branches on:
  //   Discount      → deduction = subtotal × Discount Percent / 100, capped by
  //                   Max Discount. VIO reads the PERCENTAGE here, not the
  //                   base price, so it has its own field.
  //   Cash Discount → 满减: order must reach Minimum Spend, then the base
  //                   price is deducted.
  //   Cash/Product  → neither applies.
  const renderDiscountTermsFields = (voucherType) => {
    const isPercentDiscount = voucherType === "discount";
    const isCashDiscount = voucherType === "cash_discount";
    if (!isPercentDiscount && !isCashDiscount) return null;
    return (
      <Row gutter={16}>
        {isPercentDiscount && (
          <>
            <Col xs={24} md={12}>
              <Form.Item
                name="discountPercent"
                label="Discount percent"
                required
                extra="VIO deducts this % of the order subtotal."
                rules={[
                  { required: true, message: "Discount percent is required for Discount vouchers" },
                  {
                    type: "number",
                    exclusiveMin: true,
                    min: 0,
                    max: 100,
                    message: "Must be between 0 (exclusive) and 100",
                  },
                ]}
              >
                <InputNumber
                  min={0}
                  max={100}
                  step={1}
                  addonAfter="%"
                  placeholder="e.g. 20"
                />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item
                name="maxDiscount"
                label="Max discount"
                extra="Optional cap on the deduction. Leave empty for no cap."
                rules={[{ type: "number", min: 0, message: "Must be a non-negative number" }]}
              >
                <InputNumber min={0} placeholder="e.g. 50" />
              </Form.Item>
            </Col>
          </>
        )}
        {isCashDiscount && (
          <Col xs={24} md={12}>
            <Form.Item
              name="minSpend"
              label="Minimum spend"
              extra="Order subtotal must reach this before the voucher can be used."
              rules={[{ type: "number", min: 0, message: "Must be a non-negative number" }]}
            >
              <InputNumber min={0} placeholder="e.g. 100" />
            </Form.Item>
          </Col>
        )}
      </Row>
    );
  };

  // Redemption location, shared by the create and edit modals. VIO maps a
  // present address onto Voucher.storeLocation and switches applicableScope to
  // single_store, which is what makes the member app's "Where to use" card
  // render an address with a maps link. Coordinates are optional — with them
  // the map pin is exact, without them VIO falls back to an address search.
  const renderLocationFields = () => (
    <>
      <Row gutter={16}>
        <Col xs={24}>
          <Form.Item
            name="address"
            label="Redemption address"
            extra="Shown in the VIO member app under “Where to use”. Leave empty if the voucher is not tied to one location."
            rules={[{ max: 500, message: "Address cannot exceed 500 characters" }]}
          >
            <Input placeholder="e.g. 123 Sukhumvit Rd, Khlong Toei, Bangkok" />
          </Form.Item>
        </Col>
      </Row>
      <Row gutter={16}>
        <Col xs={24} md={12}>
          <Form.Item
            name="latitude"
            label="Latitude"
            extra="Optional — gives the member app an exact map pin."
            rules={[
              { type: "number", min: -90, max: 90, message: "Latitude must be between -90 and 90" },
            ]}
          >
            <InputNumber step={0.000001} placeholder="e.g. 13.7376" />
          </Form.Item>
        </Col>
        <Col xs={24} md={12}>
          <Form.Item
            name="longitude"
            label="Longitude"
            rules={[
              { type: "number", min: -180, max: 180, message: "Longitude must be between -180 and 180" },
            ]}
          >
            <InputNumber step={0.000001} placeholder="e.g. 100.5602" />
          </Form.Item>
        </Col>
      </Row>
    </>
  );

  const returnFromPreviewModal = () => {
    if (previewLoading) {
      message.warning("Please wait for the operation to complete");
      return;
    }

    const mode = previewMode;
    setPreviewModalVisible(false);
    if (mode === "edit") {
      setEditModalVisible(true);
    } else if (mode === "create") {
      setCreateModalVisible(true);
    }
    setTimeout(() => {
      setPreviewData(null);
      setPreviewMode(null);
    }, 200);
  };

  const handlePreviewCancel = () => returnFromPreviewModal();

  const handleBackToEdit = () => returnFromPreviewModal();
  // Handle create voucher - now shows preview first
  const handleCreateVoucherSubmit = async (values) => {
    if (!croppedImageUrl) {
      message.error("Please upload and crop an image");
      return;
    }
    if (isPoolBackedVoucher(values) && !createImportFile) {
      message.error(
        `Please select a ${getConsumptionCsvCopy(values.consumptionType).singular} CSV to upload`,
      );
      return;
    }
    try {
      if (previewLoading) {
        message.warning("Please wait for the current operation to complete");
        return;
      }
      dismissQuillUi();
      createFormDraftRef.current = values;
      setPreviewData({ ...values, images: [croppedImageUrl] });
      setPreviewMode("create");
      setCreateModalVisible(false);
      setPreviewModalVisible(true);
    } catch (error) {
      console.error("Form submission error:", error);
      message.error("Failed to prepare voucher preview");
    }
  };

  // Handle create voucher confirmation from preview
  const handleCreateVoucherConfirm = async () => {
    try {
      setPreviewLoading(true);

      // Convert empty maxPurchasePerUser to null for no limit, and an empty
      // redemption window to null for "unknown"
      const processedValues = {
        ...previewData,
        maxPurchasePerUser: previewData.maxPurchasePerUser || null,
        redemptionValidityDays: previewData.redemptionValidityDays || null,
      };

      // Remove the preview image URL and use the actual file
      delete processedValues.images;

      // If there's an image, upload it first
      if (imageFile) {
        const formData = new FormData();
        formData.append("image", imageFile);

        const imageResponse = await axios.post(
          "/admin/upload-image",
          formData,
          {
            headers: {
              "Content-Type": "multipart/form-data",
            },
          },
        );

        processedValues.images = [imageResponse.data.imageUrl];
      }

      const createResponse = await axios.post(
        "/admin/vouchers",
        processedValues,
      );
      const newVoucherId = createResponse.data?.voucher?._id;

      // Pool-backed vouchers: immediately import the selected CSV pool so the
      // admin never has to revisit the edit screen.
      if (
        isPoolBackedVoucher(processedValues) &&
        createImportFile &&
        newVoucherId
      ) {
        const csvCopy = getConsumptionCsvCopy(processedValues.consumptionType);
        try {
          const codesFd = new FormData();
          codesFd.append("file", createImportFile);
          const importRes = await axios.post(
            `/admin/vouchers/${newVoucherId}/codes/import`,
            codesFd,
            { headers: { "Content-Type": "multipart/form-data" } },
          );
          const summary = importRes.data?.summary || {};
          message.success(
            `Voucher created. Imported ${summary.inserted ?? 0} ${csvCopy.singular}(s)` +
              (summary.duplicateInFile
                ? `, ${summary.duplicateInFile} duplicate(s) in file`
                : "") +
              (summary.duplicateInDb
                ? `, ${summary.duplicateInDb} already existed`
                : "") +
              (summary.invalidLines
                ? `, ${summary.invalidLines} invalid line(s) skipped`
                : ""),
          );
        } catch (importError) {
          console.error("Auto-import voucher codes error:", importError);
          // The interceptor has already shown why the import failed; this
          // adds the part it cannot know: the voucher itself was created.
          message.warning(
            `Voucher created, but importing the ${csvCopy.singular} CSV failed. Upload it from the Edit screen.`,
          );
        }
      } else {
        message.success("Voucher created successfully");
      }

      // Close modals in proper sequence with slight delay for smooth UX
      setPreviewModalVisible(false);
      setTimeout(() => {
        setCreateModalVisible(false);
        createForm.resetFields();
        createFormDraftRef.current = null;
        // Reset image states
        setImageFile(null);
        setImageSrc(null);
        setCroppedImageUrl(null);
        setCreateImportFile(null);
        setPreviewData(null);
        setPreviewMode(null);
        // Refresh vouchers list without full loading state
        fetchVouchers(true);
      }, 200);
    } catch (error) {
      console.error("Create voucher error:", error);
      // The axios interceptor already shows field-level errors for 400.
      showApiError(error, "Failed to create voucher");

      // Don't close modal on error, let user try again
    } finally {
      setPreviewLoading(false);
    }
  };

  // Handle edit voucher - now shows preview first
  const handleEditVoucherSubmit = async (values) => {
    if (
      !editCroppedImageUrl &&
      !(selectedVoucher?.images && selectedVoucher.images.length > 0)
    ) {
      message.error("Please upload and crop an image");
      return;
    }
    try {
      if (previewLoading) {
        message.warning("Please wait for the current operation to complete");
        return;
      }
      if (!selectedVoucher || !selectedVoucher._id) {
        message.error("No voucher selected for editing");
        return;
      }
      dismissQuillUi();
      editFormDraftRef.current = values;
      const markupForPreview = resolveMarkupFromFormValue(
        values.markupPercent ?? editMarkupPercent,
        selectedVoucher,
        providerRates,
      );
      setEditMarkupPercent(markupForPreview);
      setPreviewData({
        ...values,
        markupPercent: markupForPreview,
        images: [editCroppedImageUrl || selectedVoucher?.images?.[0] || ""],
        _id: selectedVoucher._id,
      });
      setPreviewMode("edit");
      setEditModalVisible(false);
      setPreviewModalVisible(true);
    } catch (error) {
      console.error("Form submission error:", error);
      message.error("Failed to prepare voucher preview");
    }
  };

  // Handle edit voucher confirmation from preview
  const handleEditVoucherConfirm = async () => {
    try {
      setPreviewLoading(true);

      // Check if previewData contains the voucher ID
      if (!previewData || !previewData._id) {
        message.error("Invalid voucher data for editing");
        return;
      }

      // Build draft values first
      const draftBasePrice = firstFiniteNumberOrZero(
        previewData.basePrice,
        editBasePrice,
      );
      const draftCostPrice = firstFiniteNumberOrZero(
        previewData.costPrice,
        editCostPrice,
      );
      const markupForSave = resolveMarkupFromFormValue(
        previewData.markupPercent ?? editMarkupPercent,
        { ...selectedVoucher, costPrice: draftCostPrice },
        providerRates,
      );

      const draftValues = {
        ...previewData,
        basePrice: draftBasePrice,
        costPrice: draftCostPrice,
        markupPercent: markupForSave,
        maxPurchasePerUser: previewData.maxPurchasePerUser || null,
        redemptionValidityDays: previewData.redemptionValidityDays || null,
      };

      // Remove non-updatable helper fields
      delete draftValues.images;
      delete draftValues._id;

      // Omit rich-text fields when normalized content unchanged (avoids false "no changes" and spurious validation)
      if (
        normalizePlainText(draftValues.description || "") ===
        normalizePlainText(selectedVoucher?.description || "")
      ) {
        delete draftValues.description;
      }
      if (
        normalizePlainText(
          normalizeTermsForDisplay(draftValues.termsAndConditions || ""),
        ) ===
        normalizePlainText(
          normalizeTermsForDisplay(selectedVoucher?.termsAndConditions || ""),
        )
      ) {
        delete draftValues.termsAndConditions;
      }

      const isDateField = (field) =>
        field === "expiryStartDatetime" || field === "expiryEndDatetime";

      const normalizeForCompare = (field, value) => {
        if (value === undefined || value === null) return "";
        if (field === "description" || field === "termsAndConditions") {
          return normalizePlainText(value);
        }
        if (field === "consumptionMessage") {
          return String(value).replace(/\r\n/g, "\n").trim();
        }
        if (isDateField(field)) {
          const d = dayjs(value);
          return d.isValid() ? String(d.valueOf()) : String(value);
        }
        if (
          field === "basePrice" ||
          field === "costPrice" ||
          field === "markupPercent" ||
          field === "maxPurchasePerUser" ||
          field === "redemptionValidityDays"
        ) {
          if (value === null || value === undefined) return "__null__";
          return value === "" ? "" : String(value);
        }
        return String(value);
      };

      const toPayloadValue = (field, value) => {
        if (value === undefined) return value;
        if (isDateField(field)) {
          const d = dayjs(value);
          return d.isValid() ? d.toISOString() : value;
        }
        return value;
      };

      // Only send changed fields so unchanged rich-text won't trigger backend validation.
      const processedValues = {};
      Object.entries(draftValues).forEach(([field, value]) => {
        let oldValue = selectedVoucher?.[field];
        let newValue = value;
        if (field === "markupPercent") {
          oldValue = resolveEffectiveMarkupPercent(selectedVoucher, providerRates);
          newValue = markupForSave;
        }
        const oldNormalized = normalizeForCompare(field, oldValue);
        const newNormalized = normalizeForCompare(field, newValue);
        if (oldNormalized !== newNormalized) {
          processedValues[field] = toPayloadValue(field, newValue);
        }
      });

      // If new images, upload first
      if (editImageFile) {
        const formData = new FormData();
        formData.append("image", editImageFile);
        const imageResponse = await axios.post(
          "/admin/upload-image",
          formData,
          {
            headers: { "Content-Type": "multipart/form-data" },
          },
        );
        processedValues.images = [imageResponse.data.imageUrl];
      }

      if (Object.keys(processedValues).length === 0) {
        message.info("No changes detected");
        setPreviewModalVisible(false);
        setEditModalVisible(true);
        return;
      }

      await axios.put(`/admin/vouchers/${previewData._id}`, processedValues);

      // Success flow with proper modal closing sequence
      message.success("Voucher updated successfully");

      // Close modals in proper sequence with slight delay for smooth UX
      setPreviewModalVisible(false);
      setTimeout(() => {
        setEditModalVisible(false);
        editForm.resetFields();
        editFormDraftRef.current = null;
        setSelectedVoucher(null);
        setPreviewData(null);
        setPreviewMode(null);
        setEditImageFile(null);
        setEditImageSrc(null);
        setEditCroppedImageUrl(null);
        setEditCropModalVisible(false);
        // Refresh voucher list
        fetchVouchers(true);
      }, 200);
    } catch (error) {
      console.error("Edit voucher error:", error);
      // The axios interceptor already shows field-level errors for 400.
      showApiError(error, "Failed to update voucher");

      // Don't close modal on error, let user try again
    } finally {
      setPreviewLoading(false);
    }
  };

  // Fetch voucher codes with pagination and filtering
  const fetchVoucherCodes = async (
    voucherId,
    page = 1,
    pageSize = 10,
    status = "all",
  ) => {
    try {
      setCodesLoading(true);

      // Build query parameters
      const params = { page, limit: pageSize };
      if (status !== "all") {
        params.status = status;
      }

      // Fetch codes with pagination and get complete statistics
      const [codesResponse, statsResponse] = await Promise.all([
        axios.get(`/admin/vouchers/${voucherId}/codes`, { params }),
        axios.get(`/admin/vouchers/${voucherId}/codes-status`),
      ]);

      setVoucherCodes(codesResponse.data.codes);
      setCodesPagination(codesResponse.data.pagination);
      setCodesStats(statsResponse.data);
    } catch (error) {
      console.error("Fetch codes error:", error);
      showApiError(error, "Failed to load voucher codes");
    } finally {
      setCodesLoading(false);
    }
  };

  // Handle codes pagination change
  const handleCodesPaginationChange = (page, pageSize = 10) => {
    if (selectedVoucher) {
      const newFilter = { ...codesFilter, page, pageSize };
      setCodesFilter(newFilter);
      fetchVoucherCodes(selectedVoucher._id, page, pageSize, newFilter.status);
    }
  };

  // Handle codes status filter change
  const handleCodesStatusFilter = (status) => {
    if (selectedVoucher) {
      const newFilter = { ...codesFilter, status, page: 1 };
      setCodesFilter(newFilter);
      fetchVoucherCodes(selectedVoucher._id, 1, newFilter.pageSize, status);
    }
  };

  // Export codes to CSV
  const exportCodesToCSV = async () => {
    if (!selectedVoucher) return;
    let hideLoading = () => {};

    try {
      setCodesLoading(true);
      hideLoading = message.loading("Preparing export...", 0);

      // Get all codes for selected statuses
      let allCodes = [];
      const statusesToExport = exportStatusOptions.includes("all")
        ? ["all"]
        : exportStatusOptions;

      // Page through results so the export doesn't silently truncate when a
      // voucher has more codes than the per-request limit. PAGE_SIZE keeps
      // each request small enough that the gateway/proxy doesn't time out.
      const PAGE_SIZE = 1000;
      const HARD_CAP = 200000;

      const fetchAllPagesForStatus = async (statusValue) => {
        const collected = [];
        let page = 1;
        while (collected.length < HARD_CAP) {
          const resp = await axios.get(
            `/admin/vouchers/${selectedVoucher._id}/codes`,
            {
              params: {
                page,
                limit: PAGE_SIZE,
                status: statusValue === "all" ? undefined : statusValue,
              },
            },
          );
          const pageCodes = resp.data?.codes || [];
          collected.push(...pageCodes);
          const totalPages =
            resp.data?.pagination?.totalPages ??
            (pageCodes.length < PAGE_SIZE ? page : page + 1);
          if (pageCodes.length < PAGE_SIZE || page >= totalPages) break;
          page += 1;
        }
        return collected;
      };

      for (const status of statusesToExport) {
        const pageData = await fetchAllPagesForStatus(status);
        if (status === "all") {
          allCodes = pageData;
          break;
        }
        allCodes = allCodes.concat(pageData);
      }

      if (allCodes.length >= HARD_CAP) {
        message.warning(
          `Export truncated to first ${HARD_CAP.toLocaleString()} codes`,
        );
      }

      // Remove duplicates if multiple statuses were selected
      if (statusesToExport.length > 1 && !statusesToExport.includes("all")) {
        const uniqueCodes = allCodes.filter(
          (code, index, self) =>
            index === self.findIndex((c) => c._id === code._id),
        );
        allCodes = uniqueCodes;
      }

      // Build CSV headers (all columns)
      const headers = [
        "Code",
        "Status",
        "Expiry Date",
        "Created Date",
        "Sold At",
        "Used By",
        "Order ID",
      ];

      // Build CSV rows (all columns)
      const rows = allCodes.map((code) => [
        code.code || "",
        code.status || "",
        code.expiryDate ? dayjs(code.expiryDate).format("YYYY-MM-DD") : "",
        code.createdAt ? dayjs(code.createdAt).format("YYYY-MM-DD HH:mm") : "",
        code.usedAt ? dayjs(code.usedAt).format("YYYY-MM-DD HH:mm") : "",
        code.usedBy || "",
        code.assignedTo?.orderId || "",
      ]);

      // Create CSV content
      const csvContent = [
        headers.join(","),
        ...rows.map((row) => row.map((field) => `"${field}"`).join(",")),
      ].join("\n");

      // Download CSV
      const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
      const link = document.createElement("a");
      const url = URL.createObjectURL(blob);
      link.setAttribute("href", url);

      const statusSuffix = exportStatusOptions.includes("all")
        ? "all"
        : exportStatusOptions.join("-");
      const safeName = (selectedVoucher.name || `voucher_${selectedVoucher._id || "unknown"}`)
        .replace(/[^a-z0-9]/gi, "_");
      const filename = `${safeName}_codes_${statusSuffix}_${dayjs().format("YYYY-MM-DD")}.csv`;
      link.setAttribute("download", filename);

      link.style.visibility = "hidden";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      // Free the blob — long-lived admin sessions exporting hundreds of
      // CSVs would otherwise accumulate them in memory.
      URL.revokeObjectURL(url);

      hideLoading();
      message.success(`Exported ${allCodes.length} codes to ${filename}`);
      setExportModalVisible(false);
    } catch (error) {
      hideLoading();
      console.error("Export error:", error);
      showApiError(error, "Failed to export codes");
    } finally {
      setCodesLoading(false);
    }
  };

  const openVoucherDetails = (voucher) => {
    setSelectedVoucher(voucher);
    setDetailsModalVisible(true);
    setCodesFilter({ status: "all", page: 1, pageSize: 10 });
    fetchVoucherCodes(voucher._id, 1, 10, "all");
  };

  // ─── Export the filtered voucher list as CSV ──────────────────────────────
  // Pulls every page that matches the current filters (not just the visible
  // one) so the file mirrors what "Set Filtered …" would act on.

  const csvCell = (value) => {
    if (value === undefined || value === null) return "";
    let text = String(value);
    // Provider-supplied names land in this file; a leading =, +, @ or a
    // non-numeric leading - makes Excel treat the cell as a formula, so prefix
    // an apostrophe to keep it literal text. Real numbers are left alone.
    if (
      /^[=+@]/.test(text) ||
      (text.startsWith("-") && !Number.isFinite(Number(text)))
    ) {
      text = `'${text}`;
    }
    return /[",\n\r]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
  };

  const csvNumber = (value, digits) => {
    // Number(null) is 0, so screen out the empty cases before coercing.
    if (value === undefined || value === null || value === "") return "";
    const num = Number(value);
    if (!Number.isFinite(num)) return "";
    return digits === undefined ? String(num) : num.toFixed(digits);
  };

  const csvDateTime = (value) => {
    if (!value) return "";
    const parsed = dayjs(value);
    return parsed.isValid() ? parsed.format("YYYY-MM-DD HH:mm:ss") : "";
  };

  const csvBool = (value) => {
    if (value === undefined || value === null) return "";
    return value ? "Yes" : "No";
  };

  const csvList = (value) =>
    Array.isArray(value)
      ? value
          .map((item) => String(item ?? "").trim())
          .filter(Boolean)
          .join(" | ")
      : "";

  const getAvailableQuantity = (voucher) =>
    voucher.availableQuantity ??
    Math.max(
      0,
      (Number(voucher.totalQuantity) || 0) - (Number(voucher.soldQuantity) || 0),
    );

  // priceHistory is admin-only and stored oldest-first ($push), so anything
  // that shows "recent changes" has to sort by changedAt instead of trusting
  // the array order.
  const getPriceHistoryNewestFirst = (voucher) => {
    const history = voucher?.priceHistory;
    if (!Array.isArray(history) || history.length === 0) return [];
    return [...history].sort(
      (a, b) => dayjs(b?.changedAt).valueOf() - dayjs(a?.changedAt).valueOf(),
    );
  };

  const getLastPriceChange = (voucher) =>
    getPriceHistoryNewestFirst(voucher)[0] || null;

  const EXPORT_COLUMNS = [
    // ── Identity ────────────────────────────────────────────────────────────
    { header: "Voucher ID", value: (v) => v._id },
    { header: "Name", value: (v) => v.name },
    { header: "Brand", value: (v) => v.brand },
    {
      header: "Provider",
      value: (v) => {
        const providerName = v.providerName;
        if (
          (!providerName || String(providerName).trim() === "") &&
          !v.isExternalVoucher
        ) {
          return formatVoucherProviderLabel(INTERNAL_VOUCHER_PROVIDER_KEY);
        }
        return formatVoucherProviderLabel(providerName);
      },
    },
    { header: "Provider Key", value: (v) => v.providerName },
    { header: "External Voucher", value: (v) => csvBool(v.isExternalVoucher) },
    { header: "External ID", value: (v) => v.externalId },

    // ── Classification ──────────────────────────────────────────────────────
    { header: "Category", value: (v) => getCategoryLabel(v.category) },
    { header: "Category (raw)", value: (v) => v.category },
    { header: "Country", value: (v) => getCountryDisplayName(v.country) },
    { header: "Country Code", value: (v) => v.country },
    { header: "Region", value: (v) => v.region },
    { header: "Address", value: (v) => v.address },
    { header: "Latitude", value: (v) => csvNumber(v.latitude) },
    { header: "Longitude", value: (v) => csvNumber(v.longitude) },
    { header: "Voucher Type", value: (v) => v.voucherType },
    { header: "Fulfillment Type", value: (v) => v.type },
    { header: "Goods Type", value: (v) => v.goodsType },
    { header: "Consumption Type", value: (v) => v.consumptionType },
    { header: "Consumption Message", value: (v) => v.consumptionMessage },
    { header: "Tags", value: (v) => csvList(v.tags) },
    { header: "Restrictions", value: (v) => csvList(v.restrictions) },

    // ── Pricing ─────────────────────────────────────────────────────────────
    { header: "Currency", value: (v) => v.currency },
    { header: "Cost Price", value: (v) => csvNumber(v.costPrice, 2) },
    { header: "Face Value", value: (v) => csvNumber(v.basePrice, 2) },
    {
      header: "Cost Discount %",
      value: (v) => csvNumber(getCostBaseDiscountPercent(v)),
    },
    {
      header: "Cost / Face Value %",
      value: (v) => csvNumber(getCostBaseRatioPercent(v)),
    },
    {
      header: "Mark Up %",
      value: (v) => csvNumber(resolveEffectiveMarkupPercent(v, providerRates)),
    },
    {
      header: "Mark Up Source",
      value: (v) => (markupPercentIsInherited(v) ? "Provider" : "Voucher"),
    },
    {
      header: "Mark Up Stored %",
      value: (v) => csvNumber(v.markupPercent),
    },
    {
      header: "Mark Up Locked",
      value: (v) => csvBool(v.markupPercentOverride),
    },
    {
      header: "Settlement Price",
      value: (v) => csvNumber(v.settlementPrice, 2),
    },
    { header: "Normalized Price", value: (v) => csvNumber(v.normalizedPrice) },
    { header: "Normalized Currency", value: (v) => v.normalizedCurrency },
    { header: "Normalized Rate", value: (v) => csvNumber(v.normalizedRate) },
    {
      header: "Normalized Updated At",
      value: (v) => csvDateTime(v.normalizedUpdatedAt),
    },
    { header: "Discount %", value: (v) => csvNumber(v.discountPercent) },
    { header: "Max Discount", value: (v) => csvNumber(v.maxDiscount) },
    { header: "Min Spend", value: (v) => csvNumber(v.minSpend) },

    // ── Stock and sales ─────────────────────────────────────────────────────
    { header: "Total Quantity", value: (v) => csvNumber(v.totalQuantity ?? 0) },
    { header: "Sold Quantity", value: (v) => csvNumber(v.soldQuantity ?? 0) },
    {
      header: "Available Quantity",
      value: (v) => csvNumber(getAvailableQuantity(v)),
    },
    {
      header: "Sold %",
      value: (v) => {
        const total = Number(v.totalQuantity) || 0;
        if (total <= 0) return "";
        return csvNumber(((Number(v.soldQuantity) || 0) / total) * 100, 2);
      },
    },
    {
      header: "Minimum Order Quantity",
      value: (v) => csvNumber(v.minimumOrderQuantity),
    },
    {
      header: "Max Purchase Per User",
      value: (v) => csvNumber(v.maxPurchasePerUser),
    },
    {
      header: "Pending Redeem",
      value: (v) => csvNumber(v.pendingRedeemQuantity ?? 0),
    },
    {
      header: "Total Redeem",
      value: (v) => csvNumber(v.totalRedeemQuantity ?? 0),
    },
    {
      header: "Completed Redeem",
      value: (v) => csvNumber(v.completePurchaseQuantity ?? 0),
    },

    // ── Status and validity ─────────────────────────────────────────────────
    { header: "Status", value: (v) => getStatusLabel(v) },
    { header: "Voucher Status (raw)", value: (v) => v.voucherStatus },
    { header: "Is Active", value: (v) => csvBool(v.isActive) },
    { header: "Expiry Start", value: (v) => csvDateTime(v.expiryStartDatetime) },
    { header: "Expiry End", value: (v) => csvDateTime(v.expiryEndDatetime) },
    {
      header: "Validity Period (days)",
      value: (v) => csvNumber(v.validity?.period),
    },
    {
      header: "Validity Expiry Date",
      value: (v) => csvDateTime(v.validity?.expiryDate),
    },
    {
      header: "Redemption Validity Days",
      value: (v) => csvNumber(v.redemptionValidityDays),
    },

    // ── Direct recharge ─────────────────────────────────────────────────────
    {
      header: "Requires Direct Order Params",
      value: (v) => csvBool(v.requiresDirectOrderParams),
    },
    { header: "Account Format", value: (v) => v.accountFormat },
    {
      header: "Account Option Groups",
      value: (v) =>
        csvList((v.accountOptions || []).map((group) => group?.key)),
    },

    // ── Content ─────────────────────────────────────────────────────────────
    { header: "Description", value: (v) => normalizePlainText(v.description) },
    {
      header: "Description Override",
      value: (v) => csvBool(v.descriptionOverride),
    },
    {
      header: "Terms And Conditions",
      value: (v) => normalizePlainText(v.termsAndConditions),
    },
    {
      header: "Terms Override",
      value: (v) => csvBool(v.termsAndConditionsOverride),
    },
    // ── Audit ───────────────────────────────────────────────────────────────
    { header: "Last Sync Date", value: (v) => csvDateTime(v.lastSyncDate) },
    { header: "Created At", value: (v) => csvDateTime(v.createdAt) },
    { header: "Updated At", value: (v) => csvDateTime(v.updatedAt) },
    {
      header: "Last Price Change At",
      value: (v) => csvDateTime(getLastPriceChange(v)?.changedAt),
    },
    {
      header: "Last Price Change Reason",
      value: (v) => getLastPriceChange(v)?.reason,
    },
  ];

  const buildVoucherCsv = (rows) => {
    const lines = [EXPORT_COLUMNS.map((col) => csvCell(col.header)).join(",")];
    rows.forEach((voucher) => {
      lines.push(
        EXPORT_COLUMNS.map((col) => csvCell(col.value(voucher))).join(","),
      );
    });
    // Leading BOM so Excel reads the non-ASCII voucher names as UTF-8.
    return `\uFEFF${lines.join("\r\n")}`;
  };

  const handleExportFilteredCsv = async () => {
    if (exportLoading) return;
    setExportLoading(true);
    const hide = message.loading("Preparing CSV export…", 0);
    try {
      const processedFilters = getProcessedFilters();
      const rows = [];
      let totalCount = null;
      let page = 1;

      // Drive the loop off the server's own numbers rather than the requested
      // page size: the backend is free to clamp `limit`, so a short page does
      // not mean the last page. Stop when the reported total is covered or a
      // page comes back empty.
      while (page <= EXPORT_MAX_PAGES) {
        const response = await axios.get("/vouchers/admin/all", {
          params: {
            page,
            limit: EXPORT_PAGE_SIZE,
            ...processedFilters,
          },
          paramsSerializer: { indexes: null },
          timeout: EXPORT_REQUEST_TIMEOUT_MS,
        });
        const payload = response.data?.data ?? response.data ?? {};
        const batch = payload.vouchers || [];
        if (batch.length === 0) break;
        rows.push(...batch);

        if (totalCount === null) {
          totalCount =
            payload.pagination?.totalCount ??
            payload.pagination?.total ??
            payload.pagination?.totalItems ??
            rows.length;
        }

        if (rows.length >= totalCount) break;
        page += 1;
      }

      if (rows.length === 0) {
        message.warning("No vouchers match the current filters");
        return;
      }

      const blob = new Blob([buildVoucherCsv(rows)], {
        type: "text/csv;charset=utf-8;",
      });
      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      link.download = `vouchers_filtered_${dayjs().format("YYYY-MM-DD_HHmmss")}.csv`;
      link.click();
      URL.revokeObjectURL(link.href);

      if (totalCount != null && rows.length < totalCount) {
        message.warning(
          `Exported ${rows.length} of ${totalCount} matching vouchers. Narrow the filters and export again to get the rest.`,
        );
      } else {
        message.success(`Exported ${rows.length} voucher(s)`);
      }
    } catch (error) {
      console.error("Export filtered vouchers error:", error);
      showApiError(error, "Failed to export the filtered vouchers");
    } finally {
      hide();
      setExportLoading(false);
    }
  };

  // ─── Presentation helpers ─────────────────────────────────────────────────

  const openCreateVoucher = () => {
    createFormDraftRef.current = null;
    setCreateModalVisible(true);
  };

  const openEditVoucher = (record) => {
    editFormDraftRef.current = null;
    setSelectedVoucher(record);
    setEditModalVisible(true);
  };

  const closeVoucherDetails = () => {
    setDetailsModalVisible(false);
    setSelectedVoucher(null);
  };

  // Edit straight from the details drawer. The drawer closes first (keeping
  // the selected voucher) so the edit modal never stacks on top of it.
  const editFromDetails = () => {
    if (!selectedVoucher) return;
    const record = selectedVoucher;
    setDetailsModalVisible(false);
    openEditVoucher(record);
  };

  const getProviderDisplay = (record) =>
    (!record?.providerName || String(record.providerName).trim() === "") &&
    !record?.isExternalVoucher
      ? formatVoucherProviderLabel(INTERNAL_VOUCHER_PROVIDER_KEY)
      : formatVoucherProviderLabel(record?.providerName);

  // Display only: shows the explicit classification and leaves a missing
  // flag visible instead of guessing.
  const getSourceLabel = (record) => {
    if (record?.isExternalVoucher === true) return "Third-party";
    if (record?.isExternalVoucher === false) return "Internal";
    return "Not classified";
  };

  const getSalesInfo = (record) => {
    const soldQty = record.soldQuantity ?? 0;
    const totalQty = record.totalQuantity ?? 0;
    const availableQty =
      record.availableQuantity ?? Math.max(0, totalQty - soldQty);
    const salesPercentage =
      totalQty > 0 ? Math.round((soldQty / totalQty) * 100) : 0;
    // Use salesMetrics if available, otherwise calculate manually
    const salesMetrics = record.salesMetrics || {};
    return {
      soldQty,
      totalQty,
      availableQty,
      pendingRedeemQty: Number(record.pendingRedeemQuantity) || 0,
      totalRedeemQty: Number(record.totalRedeemQuantity) || 0,
      completePurchaseQty: Number(record.completePurchaseQuantity) || 0,
      salesPercentage,
      isSellingWell: salesMetrics.isSellingWell ?? salesPercentage > 70,
      isLowStock:
        salesMetrics.isLowStock ??
        (totalQty > 0 && availableQty / totalQty < 0.1),
      fewLeft: availableQty < 10 && totalQty !== 0,
    };
  };

  const getRichHtml = (normalize, value) => {
    const html = normalize(value);
    return hasVisibleHtmlContent(html) ? html : "";
  };

  const getVoucherTypeLabel = (value) =>
    voucherTypeSelectOptions(value).find((option) => option.value === value)
      ?.label || value;

  const getDeliveryMethodLabel = (voucher) => {
    const consumptionType = voucher.consumptionType;
    if (consumptionType === "Coupon Code") return "Digital code";
    if (consumptionType === "QR Code") return "QR code";
    if (isSharedUrlVoucher(voucher)) return "Shared link";
    if (consumptionType === "URL") return "Direct link";
    return "Digital delivery";
  };

  const providerFormOptions = (() => {
    const list = Array.isArray(providers) ? [...providers] : [];
    if (
      !list.some(
        (p) => String(p).toLowerCase() === INTERNAL_VOUCHER_PROVIDER_KEY,
      )
    ) {
      list.unshift(INTERNAL_VOUCHER_PROVIDER_KEY);
    }
    return list.map((p) => ({
      value: p,
      label: formatVoucherProviderLabel(p),
    }));
  })();

  // Table columns
  const columns = [
    {
      title: "Voucher",
      key: "voucher",
      width: 320,
      render: (_, record) => (
        <div className="va-voucher-cell">
          <div className="va-thumb">
            {record.images && record.images.length > 0 ? (
              <Image
                src={record.images[0]}
                alt=""
                preview={false}
                fallback={IMAGE_FALLBACK}
              />
            ) : (
              <PictureOutlined aria-label="No image" />
            )}
          </div>
          <div className="va-voucher-cell__text">
            <div className="cell-main va-clamp-2" title={record.name}>
              {record.name}
            </div>
            <div className="cell-sub">{record.brand || "-"}</div>
          </div>
        </div>
      ),
    },
    {
      title: "Provider",
      dataIndex: "providerName",
      key: "providerName",
      width: 140,
      render: (_, record) => (
        <div>
          <div>{getProviderDisplay(record) || "-"}</div>
          <div className="cell-sub">{getSourceLabel(record)}</div>
        </div>
      ),
    },
    {
      title: "Category",
      dataIndex: "category",
      key: "category",
      width: 170,
      render: (_, record) => (
        <div>
          <div>{getCategoryLabel(record.category) || "-"}</div>
          <div className="cell-sub">
            {getCountryDisplayName(record.country) || "-"}
          </div>
        </div>
      ),
    },
    {
      title: "Face value",
      key: "faceValue",
      align: "right",
      width: 170,
      render: (_, record) => {
        const ratio = getCostBaseRatioPercent(record);
        const discount = getCostBaseDiscountPercent(record);
        return (
          <div>
            <div className="cell-money">
              {formatMoney(record.basePrice, record.currency)}
            </div>
            <div className="cell-sub">
              Cost {formatMoney(record.costPrice, record.currency)}
            </div>
            {ratio == null || discount == null ? (
              <div className="cell-sub">Cost discount —</div>
            ) : (
              <Tooltip title={`Cost is ${formatMarkupPercent(ratio)} of face value`}>
                <div className="cell-sub">
                  Cost discount {formatMarkupPercent(discount)}
                </div>
              </Tooltip>
            )}
          </div>
        );
      },
    },
    {
      title: "Settlement",
      key: "pricing",
      align: "right",
      width: 160,
      render: (_, record) => {
        const effective = resolveEffectiveMarkupPercent(record, providerRates);
        return (
          <div>
            <div className="cell-money">
              {record.settlementPrice != null
                ? formatMoney(record.settlementPrice, record.currency)
                : "-"}
            </div>
            <div className="cell-sub">
              {effective == null
                ? "Mark up —"
                : `Mark up ${Number(effective)}%${
                    markupPercentIsInherited(record) ? " (provider)" : ""
                  }`}
            </div>
          </div>
        );
      },
    },
    {
      title: "Stock",
      key: "salesInfo",
      width: 230,
      render: (_, record) => {
        const sales = getSalesInfo(record);
        return (
          <div>
            <div className="va-inline">
              <span
                className={
                  sales.fewLeft ? "cell-main va-text-danger" : "cell-main"
                }
              >
                {sales.availableQty} available
              </span>
              {sales.isLowStock && (
                <StatusTag tone="warning">Low stock</StatusTag>
              )}
              {sales.isSellingWell && (
                <StatusTag tone="success">Hot sale</StatusTag>
              )}
            </div>
            <div className="cell-sub">
              {sales.soldQty} of {sales.totalQty} sold ({sales.salesPercentage}%)
            </div>
            <Tooltip title="Across all clients">
              <div className="cell-sub">
                Redeem {sales.pendingRedeemQty} pending / {sales.totalRedeemQty}{" "}
                total · {sales.completePurchaseQty} redeemed
              </div>
            </Tooltip>
          </div>
        );
      },
    },
    {
      title: "Validity",
      key: "validity",
      width: 210,
      render: (_, record) => {
        // Branch on how the voucher actually expires, not on where it came
        // from: internal inventory can carry a relative window too.
        const { mode, days, isDefaulted } = resolveVoucherExpiryMode(record);

        if (mode === VOUCHER_EXPIRY_MODES.RELATIVE) {
          const shelfLabel = record.isExternalVoucher
            ? "Provider window"
            : "Listed";
          return (
            <div>
              <div className="va-inline">
                <span>{days} days after purchase</span>
                {isDefaulted && (
                  <StatusTag tone="info" dot={false}>
                    Default
                  </StatusTag>
                )}
              </div>
              <Tooltip
                title={
                  "Catalogue shelf window — how long the voucher stays listed. "
                  + "It is not the member voucher expiry, which runs from the "
                  + "purchase date."
                }
              >
                <div className="cell-sub">
                  {shelfLabel}: {formatDate(record.expiryStartDatetime)} to{" "}
                  {formatDate(record.expiryEndDatetime)}
                </div>
              </Tooltip>
            </div>
          );
        }

        return (
          <div>
            <div>Until {formatDate(record.expiryEndDatetime)}</div>
            <div className="cell-sub">
              From {formatDate(record.expiryStartDatetime)} · fixed expiry
            </div>
          </div>
        );
      },
    },
    {
      title: "Status",
      key: "status",
      width: 110,
      render: (_, record) => getStatusDisplay(record),
    },
    {
      title: "",
      key: "actions",
      width: 140,
      fixed: "right",
      align: "right",
      render: (_, record) => (
        <span className="table-actions">
          <Button
            type="link"
            size="small"
            disabled={loading || previewLoading}
            aria-label={`Open voucher details for ${record.name}`}
            onClick={(e) => {
              e.stopPropagation();
              openVoucherDetails(record);
            }}
          >
            View
          </Button>
          <Button
            type="link"
            size="small"
            icon={<EditOutlined />}
            disabled={loading || previewLoading}
            onClick={(e) => {
              e.stopPropagation();
              openEditVoucher(record);
            }}
          >
            Edit
          </Button>
        </span>
      ),
    },
  ];

  const codeColumns = [
    {
      title: "Code",
      dataIndex: "code",
      key: "code",
      width: 220,
      render: (code) => (
        <Text copyable className="cell-mono">
          {code}
        </Text>
      ),
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      width: 130,
      render: renderCodeStatus,
    },
    {
      title: "Expiry date",
      dataIndex: "expiryDate",
      key: "expiryDate",
      width: 120,
      render: formatDate,
    },
    {
      title: "Created",
      dataIndex: "createdAt",
      key: "createdAt",
      width: 120,
      render: formatDate,
    },
    {
      title: "Sold at",
      dataIndex: "usedAt",
      key: "usedAt",
      width: 120,
      render: formatDate,
    },
  ];

  const bulkMarkupVisiblePreview = getVisibleMarkupPreview();
  const bulkCostBaseDiscountVisiblePreview = getVisibleCostBaseDiscountPreview();
  const bulkMarkupTarget = Number(watchedBulkMarkupPercent);
  const hasBulkMarkupTarget =
    Number.isFinite(bulkMarkupTarget) && bulkMarkupTarget >= 0;

  const filtersApplied = hasActiveBulkMarkupFilters();
  const filtersDirty =
    filtersApplied ||
    tempFilters.category.length > 0 ||
    tempFilters.country.length > 0 ||
    tempFilters.brand.length > 0;
  const matchingVouchersLabel = `${pagination.total} voucher${
    pagination.total === 1 ? "" : "s"
  }`;

  const latestSyncAt = SYNC_PROVIDERS.map((p) => syncStatus[p])
    .filter((value) => value && dayjs(value).isValid())
    .sort((a, b) => dayjs(b).valueOf() - dayjs(a).valueOf())[0];

  // Category / country / brand use a pick-then-Apply pattern; Apply is live
  // whenever the picked set differs from the one in use (including empty,
  // which clears that filter).
  const sameSelection = (picked, applied) => {
    const current = applied || [];
    return (
      picked.length === current.length &&
      picked.every((value) => current.includes(value))
    );
  };

  const clearMultiFilter = (field) => {
    setTempFilters((prev) => ({ ...prev, [field]: [] }));
    setFilters((prev) => ({ ...prev, [field]: undefined }));
    setPagination((prev) => ({ ...prev, current: 1 }));
  };

  const renderMultiFilter = ({ field, placeholder, values, getLabel, onApply }) => (
    <Select
      mode="multiple"
      placeholder={placeholder}
      value={tempFilters[field]}
      onChange={(value) =>
        setTempFilters((prev) => ({ ...prev, [field]: value }))
      }
      onClear={() => clearMultiFilter(field)}
      allowClear
      {...responsiveFilterSelectProps}
      maxTagCount="responsive"
      optionFilterProp="label"
      options={values.map((value) => ({ value, label: getLabel(value) }))}
      popupRender={(menu) => (
        <div className="voucher-admin">
          {menu}
          <div className="va-select-apply">
            <Button
              type="primary"
              size="small"
              onClick={() => {
                onApply();
                document.activeElement.blur();
              }}
              disabled={sameSelection(tempFilters[field], filters[field])}
            >
              Apply
              {tempFilters[field].length > 0 &&
                ` (${tempFilters[field].length})`}
            </Button>
          </div>
        </div>
      )}
    />
  );

  const costDiscountPanel = (
    <div className="va-discount-panel">
      <p className="va-help">
        How much lower our cost is than face value: cost 70 / face value 100 =
        30%. Negative means cost is above face value: cost 110 / face value 100
        = -10%.
      </p>
      <Slider
        range
        min={COST_DISCOUNT_SLIDER_MIN}
        max={COST_DISCOUNT_SLIDER_MAX}
        step={1}
        value={costDiscountSliderValue}
        onChange={handleCostDiscountSliderChange}
        marks={COST_DISCOUNT_SLIDER_MARKS}
        tooltip={{ formatter: (value) => `${value}%` }}
      />
      {/* The slider spans 200 points, so typed bounds are the only
          practical way to pick a small negative range such as -3%. */}
      <div className="va-discount-inputs">
        <InputNumber
          size="small"
          min={COST_DISCOUNT_SLIDER_MIN}
          max={COST_DISCOUNT_SLIDER_MAX}
          step={1}
          value={costDiscountSliderValue[0]}
          onChange={(value) => handleCostDiscountBoundChange("min", value)}
          addonAfter="%"
          aria-label="Minimum cost discount"
        />
        <span className="muted">to</span>
        <InputNumber
          size="small"
          min={COST_DISCOUNT_SLIDER_MIN}
          max={COST_DISCOUNT_SLIDER_MAX}
          step={1}
          value={costDiscountSliderValue[1]}
          onChange={(value) => handleCostDiscountBoundChange("max", value)}
          addonAfter="%"
          aria-label="Maximum cost discount"
        />
        <Button
          type="link"
          size="small"
          onClick={clearCostDiscountFilter}
          disabled={!costDiscountFilterActive}
        >
          Clear
        </Button>
      </div>
    </div>
  );

  // Applied filters as removable chips under the filter bar.
  const activeFilterTags = [];
  if (filters.search) {
    activeFilterTags.push(
      <Tag
        key="search"
        closable
        onClose={() => setFilters((prev) => ({ ...prev, search: "" }))}
      >
        Search: {filters.search}
      </Tag>,
    );
  }
  (filters.category || []).forEach((cat) => {
    activeFilterTags.push(
      <Tag
        key={`category-${cat}`}
        closable
        onClose={() => {
          const newCategories = filters.category.filter((c) => c !== cat);
          setFilters((prev) => ({
            ...prev,
            category: newCategories.length > 0 ? newCategories : undefined,
          }));
          setTempFilters((prev) => ({ ...prev, category: newCategories }));
        }}
      >
        {getCategoryLabel(cat)}
      </Tag>,
    );
  });
  if (filters.status) {
    activeFilterTags.push(
      <Tag
        key="status"
        closable
        onClose={() => setFilters((prev) => ({ ...prev, status: undefined }))}
      >
        Status: {filters.status}
      </Tag>,
    );
  }
  (filters.country || []).forEach((country) => {
    activeFilterTags.push(
      <Tag
        key={`country-${country}`}
        closable
        onClose={() => {
          const newCountries = filters.country.filter((c) => c !== country);
          setFilters((prev) => ({
            ...prev,
            country: newCountries.length > 0 ? newCountries : undefined,
          }));
          setTempFilters((prev) => ({ ...prev, country: newCountries }));
        }}
      >
        {getCountryDisplayName(country)}
      </Tag>,
    );
  });
  (filters.brand || []).forEach((brand) => {
    activeFilterTags.push(
      <Tag
        key={`brand-${brand}`}
        closable
        onClose={() => {
          const newBrands = filters.brand.filter((b) => b !== brand);
          setFilters((prev) => ({
            ...prev,
            brand: newBrands.length > 0 ? newBrands : undefined,
          }));
          setTempFilters((prev) => ({ ...prev, brand: newBrands }));
        }}
      >
        Brand: {brand}
      </Tag>,
    );
  });
  if (filters.provider) {
    activeFilterTags.push(
      <Tag
        key="provider"
        closable
        onClose={() =>
          setFilters((prev) => ({ ...prev, provider: undefined }))
        }
      >
        Provider: {formatVoucherProviderLabel(filters.provider)}
      </Tag>,
    );
  }
  if (costDiscountFilterActive) {
    activeFilterTags.push(
      <Tag key="costDiscount" closable onClose={clearCostDiscountFilter}>
        Cost discount: {getCostDiscountFilterLabel()}
      </Tag>,
    );
  }

  const bulkMenuLabel = (title, hint) => (
    <div>
      <div>{title}</div>
      <div className="cell-sub">{hint}</div>
    </div>
  );

  const bulkMenuItems = [
    {
      type: "group",
      key: "scope",
      label: filtersApplied
        ? `${matchingVouchersLabel} match the current filters`
        : `No filters applied · ${matchingVouchersLabel}`,
      children: [
        {
          key: "export",
          icon: <DownloadOutlined />,
          disabled: exportLoading,
          label: bulkMenuLabel(
            "Export filtered CSV",
            "Every matching voucher, across all pages",
          ),
        },
        {
          key: "markup",
          icon: <PercentageOutlined />,
          disabled: bulkMarkupLoading,
          label: bulkMenuLabel(
            "Set filtered mark up",
            "Fixed mark up for matching active vouchers",
          ),
        },
        {
          key: "redemption",
          icon: <FieldTimeOutlined />,
          disabled: bulkRedemptionLoading,
          label: bulkMenuLabel(
            "Set filtered redemption window",
            "Days from purchase; by default only fills empty ones",
          ),
        },
      ],
    },
    { type: "divider" },
    {
      key: "online",
      icon: <CheckCircleOutlined />,
      disabled: bulkOnlineLoading,
      label: bulkMenuLabel(
        "Bring filtered offline vouchers online",
        "Only Offline vouchers change",
      ),
    },
  ];

  const handleBulkMenuClick = ({ key }) => {
    if (key === "export") {
      handleExportFilteredCsv();
    } else if (key === "markup") {
      bulkMarkupForm.resetFields();
      setBulkMarkupModalVisible(true);
    } else if (key === "redemption") {
      bulkRedemptionForm.resetFields();
      setBulkRedemptionModalVisible(true);
    } else if (key === "online") {
      setBulkOnlineModalVisible(true);
    }
  };

  const confirmSyncProvider = (providerName) => {
    Modal.confirm({
      title: `Sync ${PROVIDER_LABELS[providerName] || providerName} now?`,
      content:
        "This pulls the supplier catalogue into Vouchain and can take a while.",
      okText: "Sync now",
      cancelText: "Cancel",
      width: MODAL_WIDTH.sm,
      onOk: () => {
        setSyncTarget(providerName);
        return handleSyncProvider(providerName);
      },
    });
  };

  const confirmSyncAll = () => {
    Modal.confirm({
      title: "Sync all suppliers?",
      content:
        "This syncs GoWabi, Giftee, Zhongmin and IAK one after another and can take several minutes.",
      okText: "Sync all",
      cancelText: "Cancel",
      width: MODAL_WIDTH.sm,
      onOk: () => {
        setSyncTarget("all");
        return handleSyncAll();
      },
    });
  };

  // Shown in both bulk-update modals: what the action will touch.
  const renderBulkScope = (summary) => (
    <dl className="kv">
      <dt>Filters</dt>
      <dd>{summary}</dd>
      <dt>Matching vouchers</dt>
      <dd>
        <strong>{matchingVouchersLabel}</strong> across all pages
      </dd>
    </dl>
  );

  const renderImageField = ({
    imageUrl,
    uploadPropsForField,
    hasNewImage,
    onRemove,
  }) => (
    <Form.Item
      label="Voucher image"
      required
      extra={IMAGE_UPLOAD_HINT}
      rules={[{ required: true, message: "Please upload an image" }]}
    >
      <div className="va-image-field">
        {imageUrl ? (
          <img src={imageUrl} alt="Cropped preview" />
        ) : (
          <div className="va-image-field__empty">
            <PictureOutlined />
            <span>4:3 image, cropped after upload</span>
          </div>
        )}
      </div>
      <div className="va-image-actions">
        <Upload {...uploadPropsForField}>
          <Button size="small" icon={<UploadOutlined />}>
            {hasNewImage ? "Change image" : "Upload image"}
          </Button>
        </Upload>
        {hasNewImage && (
          <Button size="small" type="link" danger onClick={onRemove}>
            Remove image
          </Button>
        )}
      </div>
    </Form.Item>
  );

  const flowSteps = [{ title: "Details" }, { title: "Preview and confirm" }];

  return (
    <div className="voucher-admin">
      <PageHeader
        title="Vouchers"
        subtitle="The catalogue that syncs to VIO: pricing, stock, validity and codes for every voucher."
        extra={
          <>
            <Tooltip
              title={
                latestSyncAt
                  ? `Last supplier sync ${fromNow(latestSyncAt)}`
                  : "No supplier sync recorded yet"
              }
            >
              <Button
                icon={<SyncOutlined spin={syncLoading} />}
                onClick={() => setSyncDrawerOpen(true)}
              >
                Supplier sync
              </Button>
            </Tooltip>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={openCreateVoucher}
              disabled={loading || previewLoading}
            >
              Create voucher
            </Button>
          </>
        }
      />

      <FilterBar
        onReset={handleResetFilters}
        resetDisabled={!filtersDirty}
        footer={
          activeFilterTags.length > 0 ? (
            <div className="va-active-filters">
              <span className="va-active-filters__label">Active filters</span>
              {activeFilterTags}
            </div>
          ) : null
        }
      >
        <FilterBar.Item grow>
          <Input
            placeholder="Search name, brand or supplier"
            prefix={<SearchOutlined className="muted" />}
            value={filters.search}
            onChange={(e) => {
              setFilters((prev) => ({ ...prev, search: e.target.value }));
              setPagination((prev) => ({ ...prev, current: 1 }));
            }}
            allowClear
          />
        </FilterBar.Item>
        <FilterBar.Item width={150}>
          <Select
            placeholder="All statuses"
            value={filters.status}
            onChange={(value) => {
              setFilters((prev) => ({ ...prev, status: value }));
              setPagination((prev) => ({ ...prev, current: 1 }));
            }}
            allowClear
            {...responsiveFilterSelectProps}
            options={[
              { value: "online", label: "Online" },
              { value: "offline", label: "Offline" },
              { value: "expired", label: "Expired" },
            ]}
          />
        </FilterBar.Item>
        <FilterBar.Item width={190}>
          {renderMultiFilter({
            field: "category",
            placeholder: "All categories",
            values: categories,
            getLabel: getCategoryLabel,
            onApply: handleApplyCategory,
          })}
        </FilterBar.Item>
        <FilterBar.Item width={180}>
          {renderMultiFilter({
            field: "country",
            placeholder: "All countries",
            values: countries,
            getLabel: getCountryDisplayName,
            onApply: handleApplyCountry,
          })}
        </FilterBar.Item>
        <FilterBar.Item width={180}>
          {renderMultiFilter({
            field: "brand",
            placeholder: "All brands",
            values: brands,
            getLabel: (brand) => brand,
            onApply: handleApplyBrand,
          })}
        </FilterBar.Item>
        <FilterBar.Item width={170}>
          <Select
            placeholder="All providers"
            value={filters.provider}
            onChange={(value) => {
              setFilters((prev) => ({ ...prev, provider: value }));
              setPagination((prev) => ({ ...prev, current: 1 }));
            }}
            allowClear
            {...responsiveFilterSelectProps}
            showSearch
            optionFilterProp="label"
            options={providers.map((provider) => ({
              value: provider,
              label: formatVoucherProviderLabel(provider),
            }))}
          />
        </FilterBar.Item>
        <FilterBar.Item width="auto">
          <Popover
            title="Cost discount range"
            content={costDiscountPanel}
            trigger="click"
            placement="bottomRight"
            rootClassName="voucher-admin"
          >
            <Button
              icon={<FilterOutlined />}
              type={costDiscountFilterActive ? "primary" : "default"}
              ghost={costDiscountFilterActive}
            >
              {costDiscountFilterActive
                ? `Cost discount ${getCostDiscountFilterLabel()}`
                : "Cost discount"}
            </Button>
          </Popover>
        </FilterBar.Item>
      </FilterBar>

      <TableCard
        title="Vouchers"
        count={pagination.total}
        extra={
          <Dropdown
            menu={{ items: bulkMenuItems, onClick: handleBulkMenuClick }}
            trigger={["click"]}
            placement="bottomRight"
            disabled={loading || previewLoading}
          >
            <Button loading={exportLoading || bulkOnlineLoading}>
              Bulk actions <DownOutlined />
            </Button>
          </Dropdown>
        }
      >
        <Table
          {...tableDefaults}
          columns={columns}
          dataSource={vouchers}
          rowKey="_id"
          loading={loading}
          rowClassName={(record) =>
            getSalesInfo(record).isLowStock
              ? "row-clickable row-attention"
              : "row-clickable"
          }
          onRow={(record) => ({
            onClick: () => {
              if (loading || previewLoading) return;
              openVoucherDetails(record);
            },
          })}
          locale={{
            emptyText: (
              <Empty
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                description={
                  filtersApplied
                    ? "No vouchers match these filters"
                    : "No vouchers yet"
                }
              />
            ),
          }}
          pagination={{
            ...paginationDefaults,
            ...pagination,
            showQuickJumper: true,
            showTotal: (total, range) =>
              `${range[0]}-${range[1]} of ${total} vouchers`,
            onChange: (page, pageSize) => {
              if (loading || previewLoading) return;
              setPagination((prev) => ({ ...prev, current: page, pageSize }));
            },
            disabled: loading || previewLoading,
          }}
        />
      </TableCard>

      {/* ── Supplier sync drawer ─────────────────────────────────────── */}
      <Drawer
        title="Supplier sync"
        open={syncDrawerOpen}
        onClose={() => setSyncDrawerOpen(false)}
        width={DRAWER_WIDTH.md}
        rootClassName="voucher-admin"
      >
        <Section
          title="Supplier catalogues"
          description="A sync pulls the supplier catalogue into Vouchain and can take several minutes."
          extra={
            <Button
              size="small"
              icon={<SyncOutlined spin={syncLoading && syncTarget === "all"} />}
              disabled={syncLoading}
              onClick={confirmSyncAll}
            >
              Sync all
            </Button>
          }
        >
          <div className="va-sync-list">
            {SYNC_PROVIDERS.map((p) => (
              <div className="va-sync-row" key={p}>
                <div>
                  <div className="cell-main">{PROVIDER_LABELS[p]}</div>
                  <Tooltip
                    title={
                      syncStatus[p]
                        ? `Last synced ${formatDateTime(syncStatus[p])}`
                        : undefined
                    }
                  >
                    <div className="cell-sub">
                      {syncStatus[p]
                        ? `Last synced ${fromNow(syncStatus[p])}`
                        : "Never synced"}
                    </div>
                  </Tooltip>
                </div>
                <Button
                  size="small"
                  icon={<SyncOutlined spin={syncLoading && syncTarget === p} />}
                  disabled={syncLoading}
                  onClick={() => confirmSyncProvider(p)}
                >
                  Sync
                </Button>
              </div>
            ))}
          </div>
        </Section>

        <Section title="What a sync changes">
          <dl className="kv va-guide">
            <dt>GoWabi, Giftee</dt>
            <dd>
              Existing vouchers are updated from the provider and new provider
              items are added online. Live vouchers missing from the provider
              response are set to Offline and inactive, unless the response
              looks incomplete.
            </dd>
            <dt>Zhongmin</dt>
            <dd>
              New vouchers are added as Offline. Existing vouchers with provider
              data changes are also set to Offline for operator review. Vouchers
              missing from Zhongmin are set to Offline and inactive.
            </dd>
            <dt>NYYH scripts</dt>
            <dd>
              Matched Zhongmin rows are brought Online unless the script is run
              with skip status mode.
            </dd>
          </dl>
        </Section>

        <Section
          title="Provider mark up"
          description="Applied on every sync: settlement price = cost price × (1 + mark up %). Default 0%."
          extra={
            providerRateMode === "view" ? (
              <Button
                size="small"
                icon={<EditOutlined />}
                onClick={enterProviderRateEditMode}
                disabled={providerRatesLoading}
              >
                Edit
              </Button>
            ) : (
              <>
                <Button
                  size="small"
                  onClick={cancelProviderRateEdit}
                  disabled={providerRateSaving}
                >
                  Cancel
                </Button>
                <Button
                  size="small"
                  type="primary"
                  onClick={saveProviderRates}
                  loading={providerRateSaving}
                >
                  Save mark up
                </Button>
              </>
            )
          }
        >
          <div className="va-rate-grid">
            {PROVIDER_RATE_NAMES.map((name) => {
              const isEdit = providerRateMode === "edit";
              const currentPercent = providerRates?.[name]?.markupPercent ?? 0;
              const draftPercent =
                providerRateDraft[name] !== undefined
                  ? providerRateDraft[name]
                  : currentPercent;
              return (
                <div className="va-rate-row" key={name}>
                  <span>{PROVIDER_LABELS[name] || name}</span>
                  {isEdit ? (
                    <InputNumber
                      size="small"
                      min={0}
                      step={0.1}
                      value={draftPercent}
                      onChange={(value) =>
                        handleProviderRateDraftChange(name, value)
                      }
                      addonAfter="%"
                      disabled={providerRateSaving}
                      aria-label={`${PROVIDER_LABELS[name] || name} mark up`}
                    />
                  ) : (
                    <span className="cell-money">{`${Number(currentPercent)}%`}</span>
                  )}
                </div>
              );
            })}
          </div>
        </Section>

        <Section
          title="Zhongmin NYYH scripts"
          description="Upload the NYYH master spreadsheet and/or images, then run sync-to-db and db-to-frontend (backend/zhongmin-scripts). A run can take several minutes."
        >
          <div className="va-upload-stack">
            <Upload
              accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
              maxCount={1}
              fileList={nyyhXlsxList}
              beforeUpload={() => false}
              onChange={({ fileList }) => setNyyhXlsxList(fileList)}
              onRemove={() => setNyyhXlsxList([])}
            >
              <Button icon={<UploadOutlined />}>NYYH spreadsheet (.xlsx)</Button>
            </Upload>
            <Upload
              accept="image/*"
              multiple
              fileList={nyyhImageList}
              beforeUpload={() => false}
              onChange={({ fileList }) => setNyyhImageList(fileList)}
            >
              <Button icon={<UploadOutlined />}>NYYH images (batch)</Button>
            </Upload>
          </div>
          <div className="va-button-row">
            <Button
              type="primary"
              loading={zhongminNyyhUploadMode === "upload-run"}
              disabled={
                (!nyyhXlsxList.length && !nyyhImageList.length) ||
                !!zhongminScriptKey ||
                zhongminNyyhUploading
              }
              onClick={handleNyyhUploadAndRun}
            >
              Upload and run scripts
            </Button>
            <Button
              loading={zhongminScriptKey === "all"}
              disabled={
                (!!zhongminScriptKey && zhongminScriptKey !== "all") ||
                zhongminNyyhUploading
              }
              onClick={handleRunScriptsClick}
            >
              Run scripts only
            </Button>
          </div>
        </Section>
      </Drawer>

      {/* ── Bulk actions ─────────────────────────────────────────────── */}
      <Modal
        title="Set mark up for filtered vouchers"
        open={bulkMarkupModalVisible}
        onCancel={() => {
          if (bulkMarkupLoading) return;
          setBulkMarkupModalVisible(false);
          bulkMarkupForm.resetFields();
        }}
        okText="Update mark up"
        cancelText="Cancel"
        onOk={() => bulkMarkupForm.submit()}
        confirmLoading={bulkMarkupLoading}
        maskClosable={!bulkMarkupLoading}
        width={MODAL_WIDTH.md}
        rootClassName="voucher-admin"
      >
        <p className="va-modal-lead">
          Every active voucher matching the current filters, across all pages,
          gets this fixed mark up. Settlement price is recalculated from each
          voucher's cost price, and the vouchers keep this mark up on future
          provider syncs.
        </p>
        {!filtersApplied && (
          <Alert
            type="warning"
            showIcon
            className="va-alert"
            message="No filters are applied"
            description="This will update every active voucher. Apply provider, country, brand, category, status or search filters first if you only want a smaller set."
          />
        )}
        <Section>
          <dl className="kv">
            <dt>Filters</dt>
            <dd>{getBulkMarkupFilterSummary()}</dd>
            <dt>Matching vouchers</dt>
            <dd>
              <strong>{matchingVouchersLabel}</strong> across all pages
            </dd>
            <dt>This page</dt>
            <dd>
              {bulkMarkupVisiblePreview.visibleCount} voucher
              {bulkMarkupVisiblePreview.visibleCount === 1 ? "" : "s"}; current
              mark up {bulkMarkupVisiblePreview.currentRange}
            </dd>
            <dt>Cost discount on this page</dt>
            <dd>{bulkCostBaseDiscountVisiblePreview}</dd>
            <dt>New fixed mark up</dt>
            <dd>
              {hasBulkMarkupTarget ? (
                <strong>{formatMarkupPercent(bulkMarkupTarget)}</strong>
              ) : (
                <span className="muted">Enter a value below</span>
              )}
            </dd>
          </dl>
        </Section>
        <Section>
          <Form
            form={bulkMarkupForm}
            layout="vertical"
            onFinish={handleBulkMarkupSubmit}
            disabled={bulkMarkupLoading}
            className="va-form"
          >
            <Form.Item
              name="markupPercent"
              label="New mark up"
              rules={[
                { required: true, message: "Please enter mark up" },
                {
                  type: "number",
                  min: 0,
                  message: "Mark up must be a non-negative number",
                },
              ]}
            >
              <InputNumber min={0} step={0.1} addonAfter="%" placeholder="0" />
            </Form.Item>
            <Form.Item
              name="reason"
              label="Reason"
              rules={[
                { required: true, message: "Please enter a reason" },
                {
                  max: 200,
                  message: "Reason cannot exceed 200 characters",
                },
              ]}
            >
              <Input.TextArea
                rows={3}
                maxLength={200}
                showCount
                placeholder="e.g., Campaign margin adjustment, provider cost review"
              />
            </Form.Item>
          </Form>
        </Section>
      </Modal>

      <Modal
        title="Set redemption window for filtered vouchers"
        open={bulkRedemptionModalVisible}
        onCancel={() => {
          if (bulkRedemptionLoading) return;
          setBulkRedemptionModalVisible(false);
          bulkRedemptionForm.resetFields();
        }}
        okText="Update redemption window"
        cancelText="Cancel"
        onOk={() => bulkRedemptionForm.submit()}
        confirmLoading={bulkRedemptionLoading}
        maskClosable={!bulkRedemptionLoading}
        width={MODAL_WIDTH.md}
        rootClassName="voucher-admin"
      >
        <p className="va-modal-lead">
          Sets how many days members have to use the voucher after purchase, on
          active vouchers matching the current filters across all pages.
        </p>
        <Alert
          type={filtersApplied ? "info" : "warning"}
          showIcon
          className="va-alert"
          message={
            filtersApplied
              ? "This applies to the current filtered voucher list."
              : "No filters are applied."
          }
          description={
            "Third-party vouchers only: days from purchase. Internal vouchers are always "
            + "excluded because they use their fixed start and end dates."
          }
        />
        <Section>{renderBulkScope(getBulkMarkupFilterSummary())}</Section>
        <Section>
          <Form
            form={bulkRedemptionForm}
            layout="vertical"
            onFinish={handleBulkRedemptionSubmit}
            disabled={bulkRedemptionLoading}
            initialValues={{ onlyUnset: true }}
            className="va-form"
          >
            <Form.Item
              name="redemptionValidityDays"
              label="Redemption window"
              help="Leave empty to clear the window back to unknown"
              rules={[
                {
                  type: "number",
                  min: 1,
                  max: 3650,
                  message: "Must be between 1 and 3650 days",
                },
              ]}
            >
              <InputNumber
                min={1}
                max={3650}
                addonAfter="days from purchase"
                placeholder="e.g. 90"
              />
            </Form.Item>
            <Form.Item name="onlyUnset" valuePropName="checked">
              <Checkbox>
                Only fill vouchers that have no window yet (leave curated values alone)
              </Checkbox>
            </Form.Item>
          </Form>
        </Section>
      </Modal>

      <Modal
        title="Bring filtered offline vouchers online"
        open={bulkOnlineModalVisible}
        onCancel={() => {
          if (bulkOnlineLoading) return;
          setBulkOnlineModalVisible(false);
        }}
        maskClosable={!bulkOnlineLoading}
        width={MODAL_WIDTH.md}
        rootClassName="voucher-admin"
        footer={[
          <Button
            key="cancel"
            onClick={() => setBulkOnlineModalVisible(false)}
            disabled={bulkOnlineLoading}
          >
            Cancel
          </Button>,
          <Button
            key="confirm"
            type="primary"
            loading={bulkOnlineLoading}
            onClick={async () => {
              await handleOnlineFilteredOfflineVouchers();
              setBulkOnlineModalVisible(false);
            }}
          >
            Bring online
          </Button>,
        ]}
      >
        <p className="va-modal-lead">
          Every <strong>Offline</strong> voucher matching the current filters,
          across all pages, will be set to Online. Online and Expired vouchers
          are not changed, and the Status filter is treated as Offline for this
          action.
        </p>
        <dl className="kv">
          <dt>Filters</dt>
          <dd>{getBulkOnlineFilterSummary()}</dd>
          {filters.status ? (
            <>
              <dt>Status filter</dt>
              <dd>
                &ldquo;{filters.status}&rdquo; is ignored here; only Offline
                vouchers change.
              </dd>
            </>
          ) : (
            <>
              <dt>Matching now</dt>
              <dd>
                {matchingVouchersLabel} of any status; only the Offline ones
                change.
              </dd>
            </>
          )}
        </dl>
      </Modal>

      {/* ── Create voucher ───────────────────────────────────────────── */}
      <Modal
        {...formModalProps}
        title="Create voucher"
        open={createModalVisible}
        width={MODAL_WIDTH.lg}
        rootClassName="voucher-admin"
        afterOpenChange={(open) => {
          if (open) {
            const draft = createFormDraftRef.current;
            if (draft) {
              createForm.setFieldsValue(draft);
              createFormDraftRef.current = null;
            }
          }
        }}
        onCancel={() => {
          setCreateModalVisible(false);
          createForm.resetFields();
          createFormDraftRef.current = null;
          // Reset image states
          setImageFile(null);
          setImageSrc(null);
          setCroppedImageUrl(null);
        }}
        footer={
          <div className="sheet-footer">
            <Text type="secondary">
              Next you'll see how clients will see it, then confirm.
            </Text>
            <div className="sheet-footer__actions">
              <Button
                onClick={() => {
                  setCreateModalVisible(false);
                  createForm.resetFields();
                  // Reset image states
                  setImageFile(null);
                  setImageSrc(null);
                  setCroppedImageUrl(null);
                  setCreateImportFile(null);
                }}
              >
                Cancel
              </Button>
              <Button type="primary" onClick={() => createForm.submit()}>
                Preview voucher
              </Button>
            </div>
          </div>
        }
      >
        <Steps size="small" current={0} className="va-steps" items={flowSteps} />
        <Form
          form={createForm}
          layout="vertical"
          onFinish={handleCreateVoucherSubmit}
          className="va-form"
          scrollToFirstError
        >
          <Section title="Basics">
            <Row gutter={16}>
              <Col xs={24} md={16}>
                <Form.Item
                  name="name"
                  label="Voucher name"
                  rules={[
                    { required: true, message: "Please enter voucher name" },
                    {
                      max: 70,
                      message: "Voucher name cannot exceed 70 characters",
                    },
                  ]}
                >
                  <Input
                    placeholder="Voucher Name (70 characters max)"
                    maxLength={70}
                    showCount
                  />
                </Form.Item>
                <Row gutter={16}>
                  <Col xs={24} md={12}>
                    <Form.Item
                      name="brand"
                      label="Brand"
                      rules={[{ required: true, message: "Please enter brand" }]}
                    >
                      <Input placeholder="Enter brand name" />
                    </Form.Item>
                  </Col>
                  <Col xs={24} md={12}>
                    <Form.Item
                      name="category"
                      label="Category"
                      rules={[{ required: true, message: "Please select category" }]}
                    >
                      <Select placeholder="Select category">
                        {VOUCHER_CATEGORY_OPTIONS.map((option) => (
                          <Option key={option.value} value={option.value}>
                            {option.label}
                          </Option>
                        ))}
                      </Select>
                    </Form.Item>
                  </Col>
                  <Col xs={24}>
                    <Form.Item
                      name="providerName"
                      label="Provider"
                      initialValue={INTERNAL_VOUCHER_PROVIDER_KEY}
                      tooltip='Default "Internal vouchers" applies to platform-managed (non-synced) vouchers. Choose "WOGI" for manually uploaded WOGI vouchers. External providers (gowabi/giftee/zhongmin) are populated automatically by sync.'
                    >
                      <Select
                        placeholder="Select provider"
                        showSearch
                        optionFilterProp="label"
                        options={providerFormOptions}
                      />
                    </Form.Item>
                  </Col>
                </Row>
              </Col>
              <Col xs={24} md={8}>
                {renderImageField({
                  imageUrl: croppedImageUrl,
                  uploadPropsForField: uploadProps,
                  hasNewImage: !!croppedImageUrl,
                  onRemove: () => {
                    setCroppedImageUrl(null);
                    setImageFile(null);
                    setImageSrc(null);
                  },
                })}
              </Col>
            </Row>
          </Section>

          <Section
            title="Type and delivery"
            description="Voucher type sets how VIO applies the voucher to an order; consumption type sets what the member receives."
          >
            <Row gutter={16}>
              <Col xs={24} md={12}>
                <Form.Item
                  name="voucherType"
                  label="Voucher type"
                  rules={[
                    { required: true, message: "Please select voucher type" },
                  ]}
                >
                  <Select placeholder="Select voucher type">
                    {VOUCHER_TYPE_OPTIONS.map((option) => (
                      <Option key={option.value} value={option.value}>
                        {option.label}
                      </Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>
              <Col xs={24} md={12}>
                <Form.Item
                  name="consumptionType"
                  label="Consumption type"
                  rules={[
                    { required: true, message: "Please select consumption type" },
                  ]}
                >
                  <Select placeholder="Select consumption type">
                    {CONSUMPTION_TYPE_OPTIONS.map((option) => (
                      <Option key={option.value} value={option.value}>
                        {option.label}
                      </Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>
            </Row>
            {renderDiscountTermsFields(watchedCreateVoucherType)}
            {watchedCreateConsumptionType === "URL" && (
              <UrlModeFields
                sharedUrl={watchedCreateSharedUrl}
                urlMode={watchedCreateUrlMode}
              />
            )}
            {watchedCreateConsumptionType === "Manual" && (
              <Form.Item
                name="consumptionMessage"
                label="How to use"
                preserve={false}
                extra="Shown in VIO member app for manual voucher usage instructions."
                rules={[
                  { required: true, message: "Please enter how to use" },
                  {
                    max: 2000,
                    message: "How to use cannot exceed 2000 characters",
                  },
                ]}
              >
                <Input.TextArea
                  rows={4}
                  maxLength={2000}
                  showCount
                  placeholder="Enter manual redemption instructions shown to members"
                />
              </Form.Item>
            )}
          </Section>

          <Section title="Validity">
            <ExpiryModeSummary
              redemptionDays={watchedCreateRedemptionDays}
              isExternalVoucher={false}
            />
            <Row gutter={16}>
              <Col xs={24} md={8}>
                <Form.Item
                  name="expiryStartDatetime"
                  label={
                    hasCreateRedemptionWindow ? "Listed from" : "Valid from"
                  }
                  rules={[
                    { required: true, message: "Please select start date" },
                  ]}
                >
                  <DatePicker showTime placeholder="Select start date" />
                </Form.Item>
              </Col>
              <Col xs={24} md={8}>
                <Form.Item
                  name="expiryEndDatetime"
                  label={
                    hasCreateRedemptionWindow ? "Listed until" : "Valid until"
                  }
                  dependencies={["expiryStartDatetime"]}
                  rules={[
                    { required: true, message: "Please select end date" },
                    ({ getFieldValue }) => ({
                      validator(_, value) {
                        const start = getFieldValue("expiryStartDatetime");
                        if (!value || !start || value.isAfter(start)) {
                          return Promise.resolve();
                        }
                        return Promise.reject(
                          new Error("Valid Until must be later than Valid From"),
                        );
                      },
                    }),
                  ]}
                >
                  <DatePicker
                    showTime
                    placeholder="Select end date"
                    disabledDate={(current) => {
                      const start =
                        createForm.getFieldValue("expiryStartDatetime");
                      return (
                        start && current && current.isBefore(start, "day")
                      );
                    }}
                  />
                </Form.Item>
              </Col>
              <Col xs={24} md={8}>
                <Form.Item
                  name="redemptionValidityDays"
                  label="Redemption window"
                  tooltip={
                    "Days the member has to use the voucher, counted from "
                    + "purchase. Leave empty to expire on a fixed date instead."
                  }
                  help="Empty = fixed expiry on the date above"
                  rules={[
                    {
                      type: "number",
                      min: 1,
                      max: 3650,
                      message: "Must be between 1 and 3650 days",
                    },
                  ]}
                >
                  <InputNumber
                    min={1}
                    max={3650}
                    addonAfter="days"
                    placeholder="Fixed date"
                  />
                </Form.Item>
              </Col>
            </Row>
          </Section>

          <Section title="Market and pricing">
            <Row gutter={16}>
              <Col xs={24} md={12}>
                <Form.Item
                  name="country"
                  label="Country"
                  rules={[{ required: true, message: "Please select country" }]}
                >
                  <Select
                    placeholder="Select country"
                    showSearch
                    filterOption={(input, option) =>
                      option.children
                        .toLowerCase()
                        .indexOf(input.toLowerCase()) >= 0
                    }
                    onChange={(value) => {
                      const currency = COUNTRIES_CURRENCY_MAP[value];
                      createForm.setFieldsValue({ currency });
                    }}
                  >
                    {COUNTRY_OPTIONS.map((option) => (
                      <Option key={option.value} value={option.value}>
                        {option.label}
                      </Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>
              <Col xs={24} md={12}>
                <Form.Item
                  name="currency"
                  label="Currency"
                  tooltip="Set from the country."
                  rules={[{ required: true, message: "Please select currency" }]}
                >
                  <Select placeholder="Select currency" disabled>
                    {CURRENCY_OPTIONS.map((option) => (
                      <Option key={option.value} value={option.value}>
                        {option.label}
                      </Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>
              <Col xs={24} md={8}>
                <Form.Item
                  name="costPrice"
                  label="Cost price (buy-in)"
                  tooltip="What we pay to acquire the voucher."
                  rules={[{ required: true, message: "Please enter cost price" }]}
                >
                  <InputNumber min={0} step={0.01} placeholder="0.00" />
                </Form.Item>
              </Col>
              <Col xs={24} md={8}>
                <Form.Item
                  name="basePrice"
                  label="Face value"
                  tooltip="Original retail value printed on the voucher."
                  rules={[{ required: true, message: "Please enter face value" }]}
                >
                  <InputNumber min={0} step={0.01} placeholder="0.00" />
                </Form.Item>
              </Col>
              <Col xs={24} md={8}>
                <Form.Item
                  name="markupPercent"
                  label="Mark up"
                  tooltip="Settlement price = cost × (1 + mark up). Leave blank to inherit provider default."
                >
                  <InputNumber
                    min={0}
                    step={0.1}
                    addonAfter="%"
                    placeholder="0"
                  />
                </Form.Item>
              </Col>
            </Row>
            <TierPricingPreview
              basePrice={watchedBasePrice}
              costPrice={watchedCostPrice}
              markupPercent={watchedMarkupPercent}
              pricingTiers={pricingTiers}
              currency={watchedCreateCurrency}
            />
          </Section>

          <Section
            title="Inventory and codes"
            description={
              createUsesCsvPool
                ? "Stock comes from the uploaded CSV: one unit per value."
                : "Codes are minted on demand when orders are placed."
            }
          >
            {createUsesCsvPool && (
              <Form.Item
                label={getConsumptionCsvCopy(watchedCreateConsumptionType).uploadLabel}
                required
                extra={
                  `${watchedCreateConsumptionType} vouchers use uploaded values only (nothing is randomly generated). ` +
                  `${getConsumptionCsvCopy(watchedCreateConsumptionType).hint} ` +
                  "Total quantity is auto-detected from the uploaded file."
                }
              >
                <Alert
                  type="warning"
                  showIcon
                  className="va-alert"
                  message={`${getConsumptionCsvCopy(watchedCreateConsumptionType).uploadLabel.replace(" CSV", "")} values cannot be amended after the voucher is created.`}
                  description="Once you create the voucher, the imported values are final — they cannot be edited or removed. You can only append more later. Please double-check the CSV before creating."
                />
                <div className="va-button-row">
                  <Upload
                    accept=".csv"
                    showUploadList={false}
                    beforeUpload={(file) => {
                      handleSelectCreateCsv(file);
                      return false;
                    }}
                  >
                    <Button icon={<UploadOutlined />}>
                      {createImportFile ? "Change CSV" : "Select CSV"}
                    </Button>
                  </Upload>
                  {createImportFile && (
                    <span className="va-file-chip">
                      <Text type="success">{createImportFile.name}</Text>
                      <Button
                        type="link"
                        danger
                        size="small"
                        onClick={() => setCreateImportFile(null)}
                      >
                        Remove
                      </Button>
                    </span>
                  )}
                </div>
              </Form.Item>
            )}
            <Row gutter={16}>
              <Col xs={24} md={12}>
                <Form.Item
                  name="totalQuantity"
                  label="Total quantity"
                  extra={
                    createUsesCsvPool
                      ? `Auto-detected from the uploaded ${getConsumptionCsvCopy(watchedCreateConsumptionType).singular} CSV.`
                      : "Maximum 100,000. Codes are minted on demand when orders are placed."
                  }
                  rules={[
                    { required: true, message: "Please enter total quantity" },
                    {
                      type: "number",
                      min: 1,
                      max: 100000,
                      message: "Total quantity must be between 1 and 100,000",
                    },
                  ]}
                >
                  <InputNumber
                    min={1}
                    max={100000}
                    placeholder="Enter quantity (max 100,000)"
                    disabled={createUsesCsvPool}
                  />
                </Form.Item>
              </Col>
              <Col xs={24} md={12}>
                <Form.Item
                  name="maxPurchasePerUser"
                  label="Max purchase per user"
                  help="Leave empty for no limit"
                >
                  <InputNumber min={1} placeholder="No limit" />
                </Form.Item>
              </Col>
            </Row>
          </Section>

          <Section title="Redemption location">{renderLocationFields()}</Section>

          <Section title="Content">
            <Form.Item
              name="description"
              label=""
              rules={[
                { required: true, message: "Please enter description" },
                {
                  max: 2000,
                  message: "Description cannot exceed 2000 characters",
                },
              ]}
            >
              <RichTextEditor
                placeholder="Enter description of the voucher"
                label="Description"
                previewTitle="Description Preview"
                style={{ minHeight: "150px" }}
              />
            </Form.Item>
            <Form.Item
              name="termsAndConditions"
              label=""
              normalize={(v) => normalizeTermsForDisplay(v ?? "")}
              rules={[
                { required: true, message: "Please enter terms and conditions" },
                {
                  max: 2000,
                  message: "Terms and conditions cannot exceed 2000 characters",
                },
              ]}
            >
              <RichTextEditor
                placeholder="Enter terms and conditions of the voucher"
                label="Terms and Conditions"
                previewTitle="Terms and Conditions Preview"
                style={{ minHeight: "200px" }}
              />
            </Form.Item>
          </Section>
        </Form>
      </Modal>

      {/* ── Edit voucher ─────────────────────────────────────────────── */}
      <Modal
        {...formModalProps}
        title={
          selectedVoucher ? (
            <Space size={10} wrap>
              <span>Edit voucher</span>
              {getStatusDisplay(selectedVoucher)}
            </Space>
          ) : (
            "Edit voucher"
          )
        }
        open={editModalVisible}
        width={MODAL_WIDTH.lg}
        rootClassName="voucher-admin"
        afterOpenChange={(open) => {
          if (open && selectedVoucher) {
            const draft = editFormDraftRef.current;
            const seedValues =
              draft || buildEditFormValues(selectedVoucher, providerRates);
            editForm.setFieldsValue(seedValues);

            setEditBasePrice(
              firstFiniteNumberOrZero(
                seedValues.basePrice,
                selectedVoucher.basePrice,
                selectedVoucher.originalPrice,
                typeof selectedVoucher.normalizedPrice === "number"
                  ? selectedVoucher.normalizedPrice
                  : undefined,
              ),
            );
            setEditCostPrice(
              firstFiniteNumberOrZero(
                seedValues.costPrice,
                selectedVoucher.costPrice,
              ),
            );
            setEditMarkupPercent(
              resolveMarkupFromFormValue(
                seedValues.markupPercent,
                selectedVoucher,
                providerRates,
              ),
            );

            if (draft) editFormDraftRef.current = null;

            // Load the CSV pool stats for internal pool-backed vouchers.
            setImportCodesStats(null);
            if (
              !selectedVoucher.isExternalVoucher &&
              isPoolBackedVoucher(selectedVoucher)
            ) {
              fetchImportCodesStats(selectedVoucher._id);
            }
          }
        }}
        onCancel={() => {
          setEditModalVisible(false);
          editForm.resetFields();
          editFormDraftRef.current = null;
          setSelectedVoucher(null);
          setImportCodesStats(null);
        }}
        footer={
          <div className="sheet-footer">
            <Text type="secondary">
              Next you'll review the changes, then confirm.
            </Text>
            <div className="sheet-footer__actions">
              <Button
                onClick={() => {
                  setEditModalVisible(false);
                  editForm.resetFields();
                  setSelectedVoucher(null);
                }}
              >
                Cancel
              </Button>
              <Button type="primary" onClick={() => editForm.submit()}>
                Preview changes
              </Button>
            </div>
          </div>
        }
      >
        <Steps size="small" current={0} className="va-steps" items={flowSteps} />
        <Form
          form={editForm}
          layout="vertical"
          onFinish={handleEditVoucherSubmit}
          className="va-form"
          scrollToFirstError
        >
          <Section title="Basics">
            <Row gutter={16}>
              <Col xs={24} md={16}>
                <Form.Item
                  name="name"
                  label="Voucher name"
                  rules={[
                    { required: true, message: "Please enter voucher name" },
                    {
                      max: 70,
                      message: "Voucher name cannot exceed 70 characters",
                    },
                  ]}
                >
                  <Input
                    placeholder="Voucher Name (70 characters max)"
                    maxLength={70}
                    showCount
                  />
                </Form.Item>
                <Row gutter={16}>
                  <Col xs={24} md={12}>
                    <Form.Item
                      name="brand"
                      label="Brand"
                      rules={[{ required: true, message: "Please enter brand" }]}
                    >
                      <Input placeholder="Enter brand name" />
                    </Form.Item>
                  </Col>
                  <Col xs={24} md={12}>
                    <Form.Item
                      name="category"
                      label="Category"
                      rules={[{ required: true, message: "Please select category" }]}
                    >
                      <Select placeholder="Select category">
                        {VOUCHER_CATEGORY_OPTIONS.map((option) => (
                          <Option key={option.value} value={option.value}>
                            {option.label}
                          </Option>
                        ))}
                      </Select>
                    </Form.Item>
                  </Col>
                  <Col xs={24} md={12}>
                    <Form.Item
                      name="providerName"
                      label="Provider"
                      initialValue={INTERNAL_VOUCHER_PROVIDER_KEY}
                      tooltip='Default "Internal vouchers" applies to platform-managed (non-synced) vouchers. Choose "WOGI" for manually uploaded WOGI vouchers. External providers (gowabi/giftee/zhongmin) are populated automatically by sync.'
                    >
                      <Select
                        placeholder="Select provider"
                        showSearch
                        optionFilterProp="label"
                        options={providerFormOptions}
                      />
                    </Form.Item>
                  </Col>
                  <Col xs={24} md={12}>
                    <Form.Item name="voucherStatus" label="Status">
                      <Select>
                        <Option value="online">Online</Option>
                        <Option value="offline">Offline</Option>
                      </Select>
                    </Form.Item>
                  </Col>
                </Row>
              </Col>
              <Col xs={24} md={8}>
                {renderImageField({
                  imageUrl:
                    editCroppedImageUrl ||
                    (selectedVoucher?.images && selectedVoucher.images.length > 0
                      ? selectedVoucher.images[0]
                      : null),
                  uploadPropsForField: editUploadProps,
                  hasNewImage: !!editCroppedImageUrl,
                  onRemove: () => {
                    setEditCroppedImageUrl(null);
                    setEditImageFile(null);
                    setEditImageSrc(null);
                  },
                })}
              </Col>
            </Row>
          </Section>

          <Section
            title="Type and delivery"
            description={
              selectedVoucher?.isExternalVoucher === true
                ? "Voucher type and consumption type come from the supplier and can't be changed here."
                : "Voucher type sets how VIO applies the voucher to an order; consumption type sets what the member receives."
            }
          >
            <Row gutter={16}>
              <Col xs={24} md={12}>
                <Form.Item
                  name="voucherType"
                  label="Voucher type"
                  rules={[
                    { required: true, message: "Please select voucher type" },
                  ]}
                >
                  <Select
                    placeholder="Select voucher type"
                    disabled={selectedVoucher?.isExternalVoucher === true}
                  >
                    {voucherTypeSelectOptions(selectedVoucher?.voucherType).map((option) => (
                      <Option key={option.value} value={option.value}>
                        {option.label}
                      </Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>
              <Col xs={24} md={12}>
                <Form.Item
                  name="consumptionType"
                  label="Consumption type"
                  rules={[
                    { required: true, message: "Please select consumption type" },
                  ]}
                >
                  <Select
                    placeholder="Select consumption type"
                    disabled={selectedVoucher?.isExternalVoucher === true}
                  >
                    {consumptionTypeSelectOptions(selectedVoucher?.consumptionType).map((option) => (
                      <Option key={option.value} value={option.value}>
                        {option.label}
                      </Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>
            </Row>
            {renderDiscountTermsFields(watchedEditVoucherType)}
            {!selectedVoucher?.isExternalVoucher &&
              watchedEditConsumptionType === "URL" && (
                <UrlModeFields
                  sharedUrl={watchedEditSharedUrl}
                  urlMode={watchedEditUrlMode}
                  locked={
                    Number(selectedVoucher?.soldQuantity || 0) > 0 ||
                    Number(importCodesStats?.codes?.total || 0) > 0
                  }
                />
              )}
            {watchedEditConsumptionType === "Manual" && (
              <Form.Item
                name="consumptionMessage"
                label="How to use"
                preserve={false}
                extra="Shown in VIO member app for manual voucher usage instructions."
                rules={[
                  { required: true, message: "Please enter how to use" },
                  {
                    max: 2000,
                    message: "How to use cannot exceed 2000 characters",
                  },
                ]}
              >
                <Input.TextArea
                  rows={4}
                  maxLength={2000}
                  showCount
                  placeholder="Enter manual redemption instructions shown to members"
                />
              </Form.Item>
            )}
          </Section>

          <Section title="Validity">
            <ExpiryModeSummary
              redemptionDays={watchedEditRedemptionDays}
              isExternalVoucher={selectedVoucher?.isExternalVoucher}
            />
            <Row gutter={16}>
              <Col xs={24} md={8}>
                <Form.Item
                  name="expiryStartDatetime"
                  label={
                    hasEditRedemptionWindow ? "Listed from" : "Valid from"
                  }
                  rules={[
                    { required: true, message: "Please select start date" },
                  ]}
                >
                  <DatePicker showTime placeholder="Select start date" />
                </Form.Item>
              </Col>
              <Col xs={24} md={8}>
                <Form.Item
                  name="expiryEndDatetime"
                  label={
                    hasEditRedemptionWindow ? "Listed until" : "Valid until"
                  }
                  dependencies={["expiryStartDatetime"]}
                  rules={[
                    { required: true, message: "Please select end date" },
                    ({ getFieldValue }) => ({
                      validator(_, value) {
                        const start = getFieldValue("expiryStartDatetime");
                        if (!value || !start || value.isAfter(start)) {
                          return Promise.resolve();
                        }
                        return Promise.reject(
                          new Error("Valid Until must be later than Valid From"),
                        );
                      },
                    }),
                  ]}
                >
                  <DatePicker
                    showTime
                    placeholder="Select end date"
                    disabledDate={(current) => {
                      const start = editForm.getFieldValue("expiryStartDatetime");
                      return (
                        start && current && current.isBefore(start, "day")
                      );
                    }}
                  />
                </Form.Item>
              </Col>
              <Col xs={24} md={8}>
                <Form.Item
                  name="redemptionValidityDays"
                  label="Redemption window"
                  tooltip={
                    "Days the member has to use the voucher, counted from "
                    + "purchase. Leave empty to expire on a fixed date instead."
                  }
                  help={
                    selectedVoucher?.isExternalVoucher
                      ? `Empty = VIO's ${DEFAULT_EXTERNAL_REDEMPTION_DAYS}-day default`
                      : "Empty = fixed expiry on the date above"
                  }
                  rules={[
                    {
                      type: "number",
                      min: 1,
                      max: 3650,
                      message: "Must be between 1 and 3650 days",
                    },
                  ]}
                >
                  <InputNumber
                    min={1}
                    max={3650}
                    addonAfter="days"
                    placeholder={
                      selectedVoucher?.isExternalVoucher
                        ? `${DEFAULT_EXTERNAL_REDEMPTION_DAYS} (default)`
                        : "Fixed date"
                    }
                  />
                </Form.Item>
              </Col>
            </Row>
          </Section>

          <Section title="Market and pricing">
            <Row gutter={16}>
              <Col xs={24} md={12}>
                <Form.Item
                  name="country"
                  label="Country"
                  rules={[{ required: true, message: "Please select country" }]}
                >
                  <Select placeholder="Select country">
                    {COUNTRY_OPTIONS.map((option) => (
                      <Option key={option.value} value={option.value}>
                        {option.label}
                      </Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>
              <Col xs={24} md={12}>
                <Form.Item
                  name="currency"
                  label="Currency"
                  rules={[{ required: true, message: "Please select currency" }]}
                >
                  <Select placeholder="Select currency">
                    {CURRENCY_OPTIONS.map((option) => (
                      <Option key={option.value} value={option.value}>
                        {option.label}
                      </Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>
              <Col xs={24} md={8}>
                <Form.Item
                  name="costPrice"
                  label="Cost price (buy-in)"
                  tooltip="What we pay to acquire the voucher."
                  rules={[
                    { required: true, message: "Please enter cost price" },
                  ]}
                >
                  <InputNumber
                    min={0}
                    step={0.01}
                    placeholder="0.00"
                    onChange={(value) => {
                      setEditCostPrice(
                        value !== null && value !== undefined ? value : 0,
                      );
                    }}
                  />
                </Form.Item>
              </Col>
              <Col xs={24} md={8}>
                <Form.Item
                  name="basePrice"
                  label="Face value"
                  tooltip="Original retail value printed on the voucher."
                  rules={[{ required: true, message: "Please enter face value" }]}
                >
                  <InputNumber
                    min={0}
                    step={0.01}
                    placeholder="0.00"
                    onChange={(value) => {
                      setEditBasePrice(
                        value !== null && value !== undefined ? value : 0,
                      );
                    }}
                  />
                </Form.Item>
              </Col>
              <Col xs={24} md={8}>
                <Form.Item
                  name="markupPercent"
                  label="Mark up"
                  tooltip="Shows current markup (provider default or this voucher's fixed rate). Change only if this voucher should differ from Provider Mark Up."
                  extra="Same as provider % = follows Provider Mark Up on sync. Other % = fixed for this voucher."
                >
                  <InputNumber
                    min={0}
                    step={0.1}
                    addonAfter="%"
                    placeholder="0"
                    onChange={(value) => {
                      setEditMarkupPercent(
                        value === null || value === undefined || value === ""
                          ? null
                          : value,
                      );
                    }}
                  />
                </Form.Item>
              </Col>
            </Row>

            <TierPricingPreview
              basePrice={editBasePrice}
              costPrice={editCostPrice}
              markupPercent={resolveMarkupFromFormValue(
                watchedEditMarkupPercent ?? editMarkupPercent,
                selectedVoucher,
                providerRates,
              )}
              settlementPrice={selectedVoucher?.settlementPrice}
              pricingTiers={pricingTiers}
              currency={watchedEditCurrency}
            />

            {selectedVoucher?.priceHistory &&
              selectedVoucher.priceHistory.length > 0 && (
                <div className="va-panel">
                  <div className="va-panel__title">
                    Recent price and mark up changes
                  </div>
                  <div className="va-history">
                    {getPriceHistoryNewestFirst(selectedVoucher)
                      .slice(0, 5)
                      .map((change, index) => (
                        <div className="va-history__item" key={change._id || index}>
                          <span className="va-history__when">
                            {formatDateTime(change.changedAt)}
                          </span>{" "}
                          by {change.changedBy?.name || "Admin"}: Face value{" "}
                          {formatMoney(change.oldBasePrice, selectedVoucher.currency)} →{" "}
                          {formatMoney(change.newBasePrice, selectedVoucher.currency)}
                          {change.newSettlementPrice !== undefined &&
                            change.oldSettlementPrice !== undefined && (
                              <span>
                                {" "}· Settlement{" "}
                                {formatMoney(change.oldSettlementPrice, selectedVoucher.currency)} →{" "}
                                {formatMoney(change.newSettlementPrice, selectedVoucher.currency)}
                              </span>
                            )}
                          {(change.oldMarkupPercent !== undefined ||
                            change.newMarkupPercent !== undefined) && (
                            <span>
                              {" "}· Mark up{" "}
                              {change.oldMarkupPercent == null
                                ? "provider"
                                : `${change.oldMarkupPercent}%`}
                              {" "}→{" "}
                              {change.newMarkupPercent == null
                                ? "provider"
                                : `${change.newMarkupPercent}%`}
                            </span>
                          )}
                          {change.reason && <span> · Reason: {change.reason}</span>}
                        </div>
                      ))}
                    {selectedVoucher.priceHistory.length > 5 && (
                      <div className="va-history__item muted">
                        … and {selectedVoucher.priceHistory.length - 5} more
                        changes
                      </div>
                    )}
                  </div>
                </div>
              )}

            <Form.Item
              name="priceChangeReason"
              label="Reason for price / mark up change (optional)"
              help="Provide a reason for this pricing change to help with audit trails"
            >
              <Input.TextArea
                placeholder="e.g., Market price adjustment, Promotional pricing, Competitive pricing, etc."
                rows={2}
                maxLength={200}
                showCount
              />
            </Form.Item>
          </Section>

          <Section
            title="Inventory and codes"
            description={
              editUsesCsvPool
                ? "Stock follows the imported pool: one unit per value."
                : "Codes are minted on demand when orders are placed."
            }
          >
            {!selectedVoucher?.isExternalVoucher && editUsesCsvPool && (
              <div className="va-panel">
                <div className="va-panel__title">
                  {getConsumptionCsvCopy(watchedEditConsumptionType).poolTitle}
                </div>
                {importCodesStats ? (
                  <div className="va-pool-stats">
                    <StatusTag tone="success">
                      Available: {importCodesStats.codes?.available ?? 0}
                    </StatusTag>
                    <StatusTag tone="info">
                      Sold:{" "}
                      {(importCodesStats.codes?.soldNotRedeemed ?? 0) +
                        (importCodesStats.codes?.assigned ?? 0)}
                    </StatusTag>
                    <StatusTag tone="purple">
                      Redeemed: {importCodesStats.codes?.redeemed ?? 0}
                    </StatusTag>
                    <StatusTag>Total: {importCodesStats.codes?.total ?? 0}</StatusTag>
                  </div>
                ) : (
                  <p className="va-help">
                    No {getConsumptionCsvCopy(watchedEditConsumptionType).plural}{" "}
                    imported yet.
                  </p>
                )}
                <Upload
                  accept=".csv"
                  showUploadList={false}
                  beforeUpload={handleAppendCodesUpload}
                  disabled={importCodesUploading}
                >
                  <Button icon={<UploadOutlined />} loading={importCodesUploading}>
                    Append CSV
                  </Button>
                </Upload>
                <p className="va-help va-help--after">
                  Appends new {getConsumptionCsvCopy(watchedEditConsumptionType).plural}{" "}
                  to the pool. {getConsumptionCsvCopy(watchedEditConsumptionType).hint}{" "}
                  Duplicates are skipped and total quantity is auto-synced.
                </p>
              </div>
            )}
            <Row gutter={16}>
              <Col xs={24} md={12}>
                <Form.Item
                  name="totalQuantity"
                  label="Total quantity"
                  extra={
                    editUsesCsvPool
                      ? `Auto-synced from the imported ${getConsumptionCsvCopy(watchedEditConsumptionType).singular} pool. Use “Append CSV” above to add more.`
                      : "Maximum 100,000. Codes are minted on demand when orders are placed."
                  }
                  rules={[
                    { required: true, message: "Please enter total quantity" },
                    {
                      type: "number",
                      min: 1,
                      max: 100000,
                      message: "Total quantity must be between 1 and 100,000",
                    },
                  ]}
                >
                  <InputNumber
                    min={1}
                    max={100000}
                    placeholder="Enter quantity (max 100,000)"
                    disabled={editUsesCsvPool}
                  />
                </Form.Item>
              </Col>
              <Col xs={24} md={12}>
                <Form.Item
                  name="maxPurchasePerUser"
                  label="Max purchase per user"
                  help="Leave empty for no limit"
                >
                  <InputNumber min={1} placeholder="No limit" />
                </Form.Item>
              </Col>
            </Row>
          </Section>

          <Section title="Redemption location">{renderLocationFields()}</Section>

          <Section title="Content">
            <Form.Item
              name="description"
              label=""
              validateTrigger={["onSubmit", "onBlur"]}
              rules={[
                {
                  validator: (_, value) => {
                    const valueText = normalizePlainText(value || "");
                    const originalText = normalizePlainText(
                      selectedVoucher?.description || "",
                    );
                    if (valueText !== originalText && valueText.length > 2000) {
                      return Promise.reject(
                        new Error("Description cannot exceed 2000 characters"),
                      );
                    }
                    return Promise.resolve();
                  },
                },
              ]}
            >
              <RichTextEditor
                placeholder="Enter description of the voucher"
                label="Description"
                previewTitle="Description Preview"
                style={{ minHeight: "150px" }}
              />
            </Form.Item>
            <Form.Item
              name="termsAndConditions"
              label=""
              validateTrigger={["onSubmit", "onBlur"]}
              normalize={(v) => normalizeTermsForDisplay(v ?? "")}
              rules={[
                {
                  validator: (_, value) => {
                    const valueText = normalizePlainText(
                      normalizeTermsForDisplay(value || ""),
                    );
                    const originalText = normalizePlainText(
                      normalizeTermsForDisplay(
                        selectedVoucher?.termsAndConditions || "",
                      ),
                    );
                    if (valueText !== originalText && valueText.length > 2000) {
                      return Promise.reject(
                        new Error(
                          "Terms and conditions cannot exceed 2000 characters",
                        ),
                      );
                    }
                    return Promise.resolve();
                  },
                },
              ]}
            >
              <RichTextEditor
                placeholder="Enter terms and conditions of the voucher"
                label="Terms and Conditions"
                previewTitle="Terms and Conditions Preview"
                style={{ minHeight: "200px" }}
              />
            </Form.Item>
          </Section>
        </Form>
      </Modal>

      {/* ── Image crop (create) ──────────────────────────────────────── */}
      <Modal
        title="Crop image"
        open={cropModalVisible}
        destroyOnHidden
        onCancel={handleCropCancel}
        footer={[
          <Button key="cancel" onClick={handleCropCancel}>
            Cancel
          </Button>,
          <Button key="confirm" type="primary" onClick={handleCropComplete}>
            Apply crop
          </Button>,
        ]}
        width={MODAL_WIDTH.lg}
        centered
        zIndex={2000}
        rootClassName="voucher-admin"
      >
        <p className="va-modal-lead">
          Drag the corners to choose the visible area. The image is cropped to a
          4:3 ratio.
        </p>
        <div className="va-crop-area">
          {imageSrc && (
            <ReactCrop
              crop={crop}
              onChange={(newCrop) => setCrop(newCrop)}
              onComplete={(completedCrop) => setCompletedCrop(completedCrop)}
              aspect={4 / 3}
              minWidth={100}
              minHeight={75}
            >
              <img
                ref={imgRef}
                src={imageSrc}
                alt="Crop me"
                className="va-crop-img"
                onLoad={() => {
                  // Set initial crop when image loads
                  const { width, height } = imgRef.current;
                  const cropSize = Math.min(width * 0.8, height * 0.8);
                  const newCrop = {
                    unit: "px",
                    width: cropSize,
                    height: cropSize * (3 / 4),
                    x: (width - cropSize) / 2,
                    y: (height - cropSize * (3 / 4)) / 2,
                  };
                  setCrop(newCrop);
                }}
              />
            </ReactCrop>
          )}
        </div>
      </Modal>

      {/* ── Image crop (edit) ────────────────────────────────────────── */}
      <Modal
        title="Crop image"
        open={editCropModalVisible}
        destroyOnHidden
        onCancel={handleEditCropCancel}
        footer={[
          <Button key="cancel" onClick={handleEditCropCancel}>
            Cancel
          </Button>,
          <Button key="confirm" type="primary" onClick={handleEditCropComplete}>
            Apply crop
          </Button>,
        ]}
        width={MODAL_WIDTH.lg}
        centered
        zIndex={2000}
        rootClassName="voucher-admin"
      >
        <p className="va-modal-lead">
          Drag the corners to choose the visible area. The image is cropped to a
          4:3 ratio.
        </p>
        <div className="va-crop-area">
          {editImageSrc && (
            <ReactCrop
              crop={editCrop}
              onChange={(newCrop) => setEditCrop(newCrop)}
              onComplete={(completedCrop) =>
                setEditCompletedCrop(completedCrop)
              }
              aspect={4 / 3}
              minWidth={100}
              minHeight={75}
            >
              <img
                ref={editImgRef}
                src={editImageSrc}
                alt="Crop me"
                className="va-crop-img"
              />
            </ReactCrop>
          )}
        </div>
      </Modal>

      {/* ── Export codes ─────────────────────────────────────────────── */}
      <Modal
        title="Export codes"
        open={exportModalVisible}
        onCancel={() => setExportModalVisible(false)}
        footer={[
          <Button key="cancel" onClick={() => setExportModalVisible(false)}>
            Cancel
          </Button>,
          <Button
            key="export"
            type="primary"
            icon={<DownloadOutlined />}
            onClick={exportCodesToCSV}
          >
            Export CSV
          </Button>,
        ]}
        width={MODAL_WIDTH.sm}
        rootClassName="voucher-admin"
      >
        <p className="va-modal-lead">
          Downloads the codes of{" "}
          <strong>{selectedVoucher?.name || "this voucher"}</strong> as a CSV
          with every column: code, status, expiry date, created date, sold at,
          used by and order ID.
        </p>
        <div className="va-field-label">Statuses to export</div>
        <Checkbox.Group
          value={exportStatusOptions}
          onChange={setExportStatusOptions}
          className="va-export-statuses"
        >
          <Checkbox value="all">All statuses</Checkbox>
          <Checkbox value="available">Available</Checkbox>
          <Checkbox value="sold-not-redeemed">Sold</Checkbox>
          <Checkbox value="redeemed">Redeemed</Checkbox>
          <Checkbox value="expired">Expired</Checkbox>
        </Checkbox.Group>
        <p className="va-help">
          Pick &ldquo;All statuses&rdquo; for every code, or only the statuses
          you need.
        </p>
      </Modal>

      {/* ── Preview and confirm (create / edit step 2) ───────────────── */}
      <Modal
        title={
          previewMode === "create"
            ? "Preview new voucher"
            : "Preview voucher changes"
        }
        open={previewModalVisible}
        onCancel={handlePreviewCancel}
        maskClosable={!previewLoading}
        closable={!previewLoading}
        width={MODAL_WIDTH.lg}
        rootClassName="voucher-admin"
        footer={
          <div className="sheet-footer">
            <Text type="secondary">
              {previewMode === "create"
                ? "Nothing is created until you confirm."
                : "Nothing is saved until you confirm."}
            </Text>
            <div className="sheet-footer__actions">
              <Button onClick={handleBackToEdit} disabled={previewLoading}>
                Back to edit
              </Button>
              <Button
                type="primary"
                loading={previewLoading}
                onClick={
                  previewMode === "create"
                    ? handleCreateVoucherConfirm
                    : handleEditVoucherConfirm
                }
              >
                {previewMode === "create" ? "Create voucher" : "Save changes"}
              </Button>
            </div>
          </div>
        }
      >
        {previewData &&
          (() => {
            const currency = previewData.currency || "USD";
            const cost = Number(previewData.costPrice) || 0;
            const base = Number(previewData.basePrice) || 0;
            const effectiveMarkup = resolveMarkupFromFormValue(
              previewData.markupPercent,
              {
                ...previewData,
                settlementPrice:
                  previewData.settlementPrice ??
                  selectedVoucher?.settlementPrice,
              },
              providerRates,
            );
            const providerName = String(
              previewData.providerName || selectedVoucher?.providerName || "",
            )
              .trim()
              .toLowerCase();
            const providerDefault = providerRates?.[providerName]?.markupPercent;
            const followsProvider =
              effectiveMarkup != null &&
              providerDefault != null &&
              Math.abs(Number(effectiveMarkup) - Number(providerDefault)) <
                0.0001;
            const markupLabel =
              effectiveMarkup != null
                ? `${Number(effectiveMarkup)}%${followsProvider ? " (provider)" : ""}`
                : "—";
            // Settlement is also the basis for every client tier price.
            const settlement =
              effectiveMarkup != null
                ? computeSettlementFromMarkup(cost, effectiveMarkup)
                : Number(previewData.settlementPrice) ||
                  Number(selectedVoucher?.settlementPrice) ||
                  computeSettlementFromMarkup(cost, 0);
            const sortedTiers =
              pricingTiers && pricingTiers.length > 0
                ? [...pricingTiers].sort((a, b) => b.multiplier - a.multiplier)
                : [];
            const standardTier =
              sortedTiers.find((tier) => tier.name === "basic") ||
              sortedTiers.find((tier) => Number(tier.multiplier) === 1);
            const standardPrice =
              settlement * Number(standardTier?.multiplier ?? 1);
            const stock = previewData.totalQuantity || 0;
            const hasDates =
              previewData.expiryStartDatetime && previewData.expiryEndDatetime;
            const startDate = dayjs(previewData.expiryStartDatetime);
            const showValidFrom = hasDates && startDate.isAfter(dayjs());
            const descriptionHtml = getRichHtml(
              normalizeDescriptionForDisplay,
              previewData.description,
            );
            const termsHtml = getRichHtml(
              normalizeTermsForDisplay,
              previewData.termsAndConditions,
            );

            return (
              <>
                <Steps size="small" current={1} className="va-steps" items={flowSteps} />
                <p className="va-modal-lead">
                  This is how the voucher appears to clients. Prices are shown
                  for each client tier.
                </p>

                <Section>
                  <div className="va-hero">
                    <div className="va-hero__image">
                      {previewData.images && previewData.images[0] ? (
                        <Image
                          src={previewData.images[0]}
                          alt="Voucher image"
                          fallback={IMAGE_FALLBACK}
                        />
                      ) : (
                        <div className="va-hero__missing va-text-danger">
                          <PictureOutlined />
                          <span>Image required</span>
                          <span className="muted">Upload an image to complete</span>
                        </div>
                      )}
                    </div>
                    <div>
                      <StatusTag tone="primary" dot={false}>
                        {previewData.category
                          ? getCategoryLabel(previewData.category)
                          : "Category"}
                      </StatusTag>
                      <h3 className="va-hero__title">
                        {previewData.name || "Voucher name"}
                      </h3>
                      <div className="va-hero__brand">
                        by {previewData.brand || "Brand name"}
                      </div>
                      <div className="va-hero__price">
                        {formatMoney(standardPrice, currency)}
                      </div>
                      <div className="va-hero__price-note">
                        {standardTier?.displayName || "Standard"} tier price
                      </div>
                      {sortedTiers.length > 0 && settlement > 0 && (
                        <div className="va-panel">
                          <div className="va-panel__title">Price by client tier</div>
                          <div className="va-tier-grid">
                            {sortedTiers.map((tier) => {
                              const discount = (
                                (1 - tier.multiplier) *
                                100
                              ).toFixed(0);
                              return (
                                <React.Fragment key={tier.name}>
                                  <span className="va-tier-grid__name">
                                    {tier.displayName}
                                  </span>
                                  <span className="va-tier-grid__price cell-money">
                                    {formatMoney(settlement * tier.multiplier, currency)}
                                  </span>
                                  <span className="va-tier-grid__delta">
                                    {discount === "0"
                                      ? "—"
                                      : discount > 0
                                        ? `-${discount}%`
                                        : `+${Math.abs(discount)}%`}
                                  </span>
                                </React.Fragment>
                              );
                            })}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </Section>

                <Section>
                  <div className="admin-grid-2">
                    <div>
                      <h3 className="admin-section__title va-subhead">
                        Admin pricing
                      </h3>
                      <dl className="kv">
                        <dt>Cost (buy-in)</dt>
                        <dd className="cell-money">{formatMoney(cost, currency)}</dd>
                        <dt>Face value</dt>
                        <dd className="cell-money">{formatMoney(base, currency)}</dd>
                        <dt>Cost mark up</dt>
                        <dd>
                          <strong>{markupLabel}</strong>
                        </dd>
                        <dt>Settlement</dt>
                        <dd className="cell-money">
                          {formatMoney(settlement, currency)}
                        </dd>
                      </dl>
                    </div>
                    <div>
                      <h3 className="admin-section__title va-subhead">
                        Listing details
                      </h3>
                      <dl className="kv">
                        <dt>Region</dt>
                        <dd>
                          {previewData.country
                            ? getCountryDisplayName(previewData.country)
                            : "Country"}
                        </dd>
                        <dt>Available stock</dt>
                        <dd className={stock < 10 ? "va-text-danger" : "va-text-success"}>
                          {stock} {stock === 1 ? "voucher" : "vouchers"}
                        </dd>
                        {showValidFrom && (
                          <>
                            <dt>Valid from</dt>
                            <dd>{formatDate(previewData.expiryStartDatetime)}</dd>
                          </>
                        )}
                        {hasDates && (
                          <>
                            <dt>Valid until</dt>
                            <dd>{formatDate(previewData.expiryEndDatetime)}</dd>
                          </>
                        )}
                        <dt>Voucher type</dt>
                        <dd>
                          {previewData.voucherType
                            ? getVoucherTypeLabel(previewData.voucherType)
                            : "Digital voucher"}
                        </dd>
                        <dt>Delivery method</dt>
                        <dd>{getDeliveryMethodLabel(previewData)}</dd>
                        {isSharedUrlVoucher(previewData) && (
                          <>
                            <dt>Shared link</dt>
                            <dd>
                              <Typography.Link
                                href={previewData.sharedRedemptionUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                              >
                                {previewData.sharedRedemptionUrl}
                              </Typography.Link>
                            </dd>
                          </>
                        )}
                      </dl>
                    </div>
                  </div>
                </Section>

                <Section title="About this voucher">
                  <div
                    className="va-rich voucher-rich-content voucher-rich-text-line-breaks"
                    dangerouslySetInnerHTML={{ __html: descriptionHtml }}
                  />
                </Section>

                {previewData.consumptionType === "Manual" && (
                  <Section title="How to use">
                    <div className="va-rich va-rich--plain">
                      {String(previewData.consumptionMessage || "").trim() || "—"}
                    </div>
                  </Section>
                )}

                <Section title="Terms and conditions">
                  <div
                    className="va-rich voucher-rich-content voucher-rich-text-line-breaks"
                    dangerouslySetInnerHTML={{ __html: termsHtml }}
                  />
                </Section>
              </>
            );
          })()}
      </Modal>

      {/* ── Voucher details drawer ───────────────────────────────────── */}
      <Drawer
        title={
          selectedVoucher ? (
            <Space size={10} wrap>
              <span>{selectedVoucher.name}</span>
              {getStatusDisplay(selectedVoucher)}
            </Space>
          ) : (
            "Voucher"
          )
        }
        open={detailsModalVisible}
        onClose={closeVoucherDetails}
        width={DRAWER_WIDTH.xl}
        destroyOnHidden
        rootClassName="voucher-admin"
        footer={
          <div className="sheet-footer">
            <Text type="secondary">
              {selectedVoucher?.updatedAt
                ? `Updated ${fromNow(selectedVoucher.updatedAt)}`
                : ""}
            </Text>
            <div className="sheet-footer__actions">
              <Button onClick={closeVoucherDetails}>Close</Button>
              <Button
                type="primary"
                icon={<EditOutlined />}
                onClick={editFromDetails}
                disabled={!selectedVoucher || loading || previewLoading}
              >
                Edit voucher
              </Button>
            </div>
          </div>
        }
      >
        {selectedVoucher &&
          (() => {
            const effectiveMarkup = resolveEffectiveMarkupPercent(
              selectedVoucher,
              providerRates,
            );
            const availableQty =
              selectedVoucher.availableQuantity ??
              selectedVoucher.totalQuantity - (selectedVoucher.soldQuantity || 0);
            const descriptionHtml = getRichHtml(
              normalizeDescriptionForDisplay,
              selectedVoucher.description,
            );
            const termsHtml = getRichHtml(
              normalizeTermsForDisplay,
              selectedVoucher.termsAndConditions,
            );
            const codeCounts = codesStats?.codesStatus;

            const overview = (
              <>
                <Section>
                  <div className="va-hero">
                    <div className="va-hero__image">
                      {selectedVoucher.images?.[0] ? (
                        <Image
                          src={selectedVoucher.images[0]}
                          alt={selectedVoucher.name}
                          fallback={IMAGE_FALLBACK}
                        />
                      ) : (
                        <div className="va-hero__missing">
                          <PictureOutlined />
                          <span>No image</span>
                        </div>
                      )}
                    </div>
                    <dl className="kv">
                      <dt>Brand</dt>
                      <dd>{selectedVoucher.brand || "-"}</dd>
                      <dt>Provider</dt>
                      <dd>
                        {getProviderDisplay(selectedVoucher) || "-"}{" "}
                        <span className="muted">
                          · {getSourceLabel(selectedVoucher)}
                        </span>
                      </dd>
                      <dt>Category</dt>
                      <dd>{getCategoryLabel(selectedVoucher.category) || "-"}</dd>
                      <dt>Country</dt>
                      <dd>{getCountryDisplayName(selectedVoucher.country) || "-"}</dd>
                      <dt>Currency</dt>
                      <dd>{selectedVoucher.currency || "-"}</dd>
                      <dt>Voucher type</dt>
                      <dd>{selectedVoucher.voucherType || "—"}</dd>
                      <dt>Consumption type</dt>
                      <dd>{selectedVoucher.consumptionType || "—"}</dd>
                      {selectedVoucher.providerName === "zhongmin" && (
                        <>
                          <dt>Zhongmin type</dt>
                          <dd>
                            {String(selectedVoucher.type || "").trim() ? (
                              String(selectedVoucher.type).trim()
                            ) : (
                              <span className="va-placeholder-box" aria-hidden />
                            )}
                          </dd>
                        </>
                      )}
                      {selectedVoucher.externalId && (
                        <>
                          <dt>External ID</dt>
                          <dd>
                            <Text copyable className="cell-mono">
                              {selectedVoucher.externalId}
                            </Text>
                          </dd>
                        </>
                      )}
                      <dt>Voucher ID</dt>
                      <dd>
                        <Text copyable className="cell-mono">
                          {selectedVoucher._id}
                        </Text>
                      </dd>
                    </dl>
                  </div>
                </Section>

                <Section title="Pricing">
                  <dl className="kv">
                    <dt>Cost price (buy-in)</dt>
                    <dd className="cell-money">
                      {formatMoney(selectedVoucher.costPrice, selectedVoucher.currency)}
                    </dd>
                    <dt>Face value</dt>
                    <dd className="cell-money">
                      {formatMoney(selectedVoucher.basePrice, selectedVoucher.currency)}
                    </dd>
                    <dt>Cost mark up</dt>
                    <dd>
                      {effectiveMarkup == null
                        ? "—"
                        : `${Number(effectiveMarkup)}%${
                            markupPercentIsInherited(selectedVoucher)
                              ? " (provider)"
                              : ""
                          }`}
                    </dd>
                    <dt>Settlement price</dt>
                    <dd>
                      <span className="cell-money">
                        {selectedVoucher.settlementPrice != null
                          ? formatMoney(
                              selectedVoucher.settlementPrice,
                              selectedVoucher.currency,
                            )
                          : "-"}
                      </span>{" "}
                      <span className="muted">cost × (1 + mark up)</span>
                    </dd>
                  </dl>
                  <TierPricingPreview
                    basePrice={selectedVoucher.basePrice}
                    costPrice={selectedVoucher.costPrice}
                    markupPercent={effectiveMarkup}
                    settlementPrice={selectedVoucher.settlementPrice}
                    pricingTiers={pricingTiers}
                    currency={selectedVoucher.currency}
                    className="va-panel--spaced"
                  />
                </Section>

                <Section title="Stock and sales">
                  <dl className="kv">
                    <dt>Sold</dt>
                    <dd>
                      {selectedVoucher.soldQuantity || 0} of{" "}
                      {selectedVoucher.totalQuantity} total
                    </dd>
                    <dt>Available</dt>
                    <dd className={availableQty < 10 ? "va-text-danger" : "va-text-success"}>
                      {availableQty} available
                    </dd>
                    <dt>Redeem (all clients)</dt>
                    <dd>
                      {Number(selectedVoucher.pendingRedeemQuantity) || 0} pending /{" "}
                      {Number(selectedVoucher.totalRedeemQuantity) || 0} total
                    </dd>
                    <dt>Redeemed (all clients)</dt>
                    <dd>{Number(selectedVoucher.completePurchaseQuantity) || 0}</dd>
                    <dt>Max purchase per user</dt>
                    <dd>
                      {selectedVoucher.maxPurchasePerUser == null
                        ? "No limit"
                        : selectedVoucher.maxPurchasePerUser}
                    </dd>
                  </dl>
                </Section>

                <Section title="Validity">
                  <dl className="kv">
                    <dt>Member expiry</dt>
                    <dd>
                      {selectedVoucher.isExternalVoucher ? (
                        <span className="va-inline">
                          <span>
                            {selectedVoucher.redemptionValidityDays ?? 30} days
                            from purchase
                          </span>
                          {selectedVoucher.redemptionValidityDays == null && (
                            <StatusTag tone="info" dot={false}>
                              Default
                            </StatusTag>
                          )}
                        </span>
                      ) : (
                        <span>
                          Fixed until{" "}
                          {formatDate(selectedVoucher.expiryEndDatetime)}
                        </span>
                      )}
                    </dd>
                  </dl>
                </Section>
              </>
            );

            const content = (
              <>
                <Section title="Description">
                  {descriptionHtml ? (
                    <div
                      className="va-rich voucher-rich-content voucher-rich-text-line-breaks"
                      dangerouslySetInnerHTML={{ __html: descriptionHtml }}
                    />
                  ) : (
                    <div className="va-rich va-empty-box">No description</div>
                  )}
                </Section>

                {selectedVoucher.consumptionType === "Manual" && (
                  <Section title="How to use">
                    <div className="va-rich va-rich--plain">
                      {String(selectedVoucher.consumptionMessage || "").trim() ||
                        "—"}
                    </div>
                  </Section>
                )}

                {isSharedUrlVoucher(selectedVoucher) && (
                  <Section
                    title="Shared redemption link"
                    description="Every unit of this voucher hands out this same link."
                  >
                    <Typography.Link
                      href={selectedVoucher.sharedRedemptionUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {selectedVoucher.sharedRedemptionUrl}
                    </Typography.Link>
                  </Section>
                )}

                {/* Terms and conditions — always shown; placeholder when empty */}
                <Section title="Terms and conditions">
                  {termsHtml ? (
                    <div
                      className="va-rich voucher-rich-content voucher-rich-text-line-breaks"
                      dangerouslySetInnerHTML={{ __html: termsHtml }}
                    />
                  ) : (
                    <div className="va-rich va-empty-box">
                      No terms and conditions
                    </div>
                  )}
                </Section>
              </>
            );

            const codes = (
              <Section
                title="Issued codes"
                description="Codes are issued on demand at order time."
                extra={
                  codesStats ? (
                    <Button
                      size="small"
                      icon={<DownloadOutlined />}
                      onClick={() => setExportModalVisible(true)}
                    >
                      Export CSV
                    </Button>
                  ) : null
                }
              >
                {codeCounts && (
                  <div className="va-code-toolbar">
                    <Segmented
                      value={codesFilter.status}
                      onChange={handleCodesStatusFilter}
                      options={[
                        { value: "all", label: <>All<span className="va-count">{codeCounts.totalCodes}</span></> },
                        { value: "available", label: <>Available<span className="va-count">{codeCounts.availableCodes}</span></> },
                        { value: "sold-not-redeemed", label: <>Sold<span className="va-count">{codeCounts.assignedCodes}</span></> },
                        { value: "redeemed", label: <>Redeemed<span className="va-count">{codeCounts.redeemedCodes}</span></> },
                        { value: "expired", label: <>Expired<span className="va-count">{codeCounts.expiredCodes}</span></> },
                      ]}
                    />
                    {typeof codeCounts.capacityRemaining === "number" && (
                      <span className="muted">
                        Capacity left: {codeCounts.capacityRemaining}
                      </span>
                    )}
                  </div>
                )}
                <div className="va-code-table">
                  <Table
                    size="small"
                    dataSource={voucherCodes || []}
                    loading={codesLoading}
                    rowKey="_id"
                    columns={codeColumns}
                    scroll={{ x: "max-content" }}
                    locale={{
                      emptyText: (
                        <Empty
                          image={Empty.PRESENTED_IMAGE_SIMPLE}
                          description={
                            codesFilter.status === "all"
                              ? "No codes issued for this voucher yet"
                              : "No codes with this status"
                          }
                        />
                      ),
                    }}
                    pagination={
                      codesPagination
                        ? {
                            current: codesPagination.currentPage,
                            total: codesPagination.totalCount,
                            pageSize: 10,
                            size: "small",
                            showSizeChanger: false,
                            showQuickJumper: true,
                            showTotal: (total, range) => {
                              const statusText =
                                codesFilter.status === "all"
                                  ? "total"
                                  : CODE_STATUS[codesFilter.status]?.label.toLowerCase() ||
                                    codesFilter.status;
                              return `${range[0]}-${range[1]} of ${total} ${statusText} codes`;
                            },
                            onChange: (page) =>
                              handleCodesPaginationChange(page, 10),
                          }
                        : false
                    }
                  />
                </div>
              </Section>
            );

            return (
              <Tabs
                defaultActiveKey="overview"
                items={[
                  { key: "overview", label: "Overview", children: overview },
                  { key: "content", label: "Content", children: content },
                  {
                    key: "codes",
                    label: (
                      <>
                        Codes
                        {codeCounts && (
                          <span className="va-count">{codeCounts.totalCodes}</span>
                        )}
                      </>
                    ),
                    children: codes,
                  },
                ]}
              />
            );
          })()}
      </Drawer>
    </div>
  );
};

export default VoucherManagement;
